import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useForm, FormProvider } from "react-hook-form";
import cartService, {
  seedCartWriteSnapshot,
} from "../../services/cart.service";
import { withTermsAcceptedAttribute } from "../../utils/terms-cart-attribute";
import { formatStorefrontMoneyFromCents } from "../../utils/format-storefront-money";
import { getShopifyCartUrl, getShopifyRoutesRoot } from "../../utils/shopify-cart-url";
import {
  CART_LINE_THUMB_CDN_WIDTH,
  CART_LINE_THUMB_DISPLAY_PX,
  getCartLineItemThumbUrl,
  preloadCartLineThumbImages,
} from "../../utils/shopify-cdn-image";
import { enrichCartWithCompareAtPrices, applyCachedCompareAtPrices, mergeCompareAtOntoCart } from "../../utils/cart-line-compare-at";
import { useApp } from "../../context/AppState";
import { extractApplicableCartDiscountCodes, hasAppliedCartDiscountCode } from "../../../shared/cart-discount-codes.js";
import { getCartLineItemPricing } from "../../../shared/cart-line-pricing.js";
import { getCartLineDiscountLabels } from "../../../shared/cart-discount-display.js";
import {
  BUY_NOW_ACTION_BREEZE,
  BUY_NOW_ACTION_GOKWIK,
  BUY_NOW_ACTION_RAZORPAY,
  BUY_NOW_ACTION_SHIPROCKET,
  normalizeCheckoutDestinationAction,
} from "../../../shared/product-upsell-recommendation-defaults.js";
import {
  tryClickThemeCheckoutButton,
  tryOpenBreezeCheckout,
  tryOpenGokwikCheckout,
  tryOpenShiprocketCheckout,
} from "../../../shared/third-party-checkout.js";
import {
  showCartAddError,
} from "../../utils/cart-add-toast";
import {
  getCustomCartDrawerLayout,
  getStaticCartDrawerSections,
  getStaticCartDrawerSteps,
  isCustomCartDrawerThemeUpsellInjectMode,
  STATIC_CART_SECTION_INDEX,
} from "../../constants/custom-cart-drawer-template";
import { resolveCartDrawerTermsAgreement } from "../../../shared/custom-cart-drawer-template.js";
import {
  readCartDrawerDesignTokens,
  resolveTotalMrpLabelText,
  sanitizeMerchantCustomCss,
} from "../../../shared/cart-drawer-design-tokens.js";
import {
  getCartDrawerFlowCss,
  getCartDrawerHeaderTitleTypography,
  stripContainerSpacing,
} from "../../utils/sectionBlockCss";
import { getStaticCartDrawerShellStyles } from "../../utils/staticCartDrawerShellStyles";
import { promoteWeupsellShadowHostStack, setThirdPartyFloatersHiddenForCartDrawer } from "../../utils/promote-weupsell-shadow-host";
import { buildFontFamilyCssVar, buildFontFamilyStyle } from "../../helper";
import DividerBlock from "../Preview/Draggable/Block/DividerBlock";
import { isSectionVisible } from "../../../shared/section-visibility.js";
import { shouldShowCartDrawerUpsellBlock } from "../../../shared/cart-drawer-upsell-conditions.js";
import {
  ackSelectBlockToParent,
  isDeselectBlockMessage,
  parseSelectBlockMessage,
} from "../../utils/preview-selection-messages";
import { getPreviewBlockOutlineStyle } from "../../utils/preview-block-highlight";
import CartDrawerUpsellStrip from "./CartDrawerUpsellStrip";
import RewardBar from "./RewardBar";
import CartBillSummarySheet from "./CartBillSummarySheet";
import { useCartDrawerFooterActions } from "./CartDrawerFooterActions";
import AiBrandAttribution from "../../layouts/ai-recommendation-upsell/components/AiBrandAttribution";
import {
  buildCartBillSummary,
  formatSavingsBannerText,
} from "../../utils/cart-bill-summary";
import {
  useRewardBarRuntime,
  syncRewardProductCartLines,
  getLineRewardTierId,
  getRewardProductsForTier,
  isPendingAutoClaimGiftLine,
} from "../../utils/reward-bar-runtime";
import FreeGiftPickerSheet, {
  useFreeGiftPickerController,
} from "./FreeGiftPickerSheet";
import CartDrawerVariantSelect from "./CartDrawerVariantSelect";
import { useCartDrawerUpsellDiscountSync } from "../../utils/cart-drawer-upsell-discount-sync";
import {
  isLinkToProductEnabled,
  isLinkToProductOpenInNewTab,
  openProductPage,
  resolveCartLineProductUrl,
} from "../../../shared/product-link.js";
import {
  resolveRewardProductVariantId,
  computeRewardBarState,
  resolveRewardGiftLineDisplayPricing,
  isSpecificProductsRewardMode,
  DEFAULT_FREE_GIFT_REOPEN_BANNER_TEXT,
  DEFAULT_FREE_GIFT_REOPEN_BANNER_BUTTON,
  REWARD_TIER_TYPE,
} from "../../../shared/reward-bar.js";
import { getRewardBarMatchOptions } from "../../utils/reward-bar-runtime";
import { extractShopifyNumericId } from "../../../shared/checkout-tracking.js";
import confetti from "canvas-confetti";
import "./custom-cart-drawer.css";

const DISCOUNT_BADGE_SRC =
  "https://cdn.shopify.com/extensions/019f8931-6926-7362-bb6e-46cd808ae361/shiprocket-smart-cart-247/assets/discount_badge.svg";

/** Last-resort thumb when a free-gift product has no catalog/cart image. */
const FREE_GIFT_FALLBACK_IMAGE =
  "https://cdn.shopify.com/s/files/1/0597/9422/7350/files/Free_Gift_01.jpg?v=1756148739";

const ANNOUNCEMENT_ROTATE_MS = 3500;

function mergeEditorPartStyle(baseStyle, { embeddedInEditor, isSelected, isHovered }) {
  if (!embeddedInEditor) return baseStyle;
  return {
    ...baseStyle,
    ...getPreviewBlockOutlineStyle({
      isSelected,
      isHovered,
      isPreviewMode: true,
    }),
    cursor: "pointer",
  };
}

function editorPartA11yProps(embeddedInEditor) {
  if (!embeddedInEditor) return {};
  return { role: "button", tabIndex: 0 };
}

/** SVG Repo cart-plus — https://www.svgrepo.com/svg/533042/cart-plus */
const EmptyCartIcon = () => (
  <svg
    className="weu-custom-cart-drawer__empty-icon"
    width="48"
    height="48"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden
  >
    <path
      d="M21 5L19 12H7.37671M20 16H8L6 3H3M16 5.5H13.5M13.5 5.5H11M13.5 5.5V8M13.5 5.5V3M9 20C9 20.5523 8.55228 21 8 21C7.44772 21 7 20.5523 7 20C7 19.4477 7.44772 19 8 19C8.55228 19 9 19.4477 9 20ZM20 20C20 20.5523 19.5523 21 19 21C18.4477 21 18 20.5523 18 20C18 19.4477 18.4477 19 19 19C19.5523 19 20 19.4477 20 20Z"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const CloseIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
    <path d="M19 6.41 17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
  </svg>
);

const DeleteIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
    <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z" />
  </svg>
);

const MinusIcon = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
    <path d="M19 13H5v-2h14v2z" />
  </svg>
);

const PlusIcon = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
    <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z" />
  </svg>
);

const LockIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
    <path d="M18 8h-1V6a5 5 0 0 0-10 0v2H6a2 2 0 0 0-2 2v10a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V10a2 2 0 0 0-2-2Zm-7 8.73V17h2v-.27a2 2 0 1 0-2 0ZM9 8V6a3 3 0 0 1 6 0v2H9Z" />
  </svg>
);

const SparkleIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
    <path d="m12 2 1.2 4.2L17.4 7.4 13.2 8.6 12 12.8 10.8 8.6 6.6 7.4l4.2-1.2L12 2Zm7 9 1 3.5L23.5 15.5 20 16.5 19 20l-1-3.5L14.5 15.5 18 14.5 19 11Zm-14 0 1-3.5L.5 15.5 4 16.5 5 20l1-3.5L9.5 15.5 6 14.5 5 11Z" />
  </svg>
);

const GiftBadgeIcon = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
    <path d="M20 7h-2.18A3 3 0 0 0 15 4a3 3 0 0 0-3 2.82A3 3 0 0 0 9 4a3 3 0 0 0-2.82 3H4a1 1 0 0 0-1 1v3h18V8a1 1 0 0 0-1-1ZM9 6a1 1 0 1 1 0 2H7.82A1 1 0 0 1 9 6Zm6 0a1 1 0 0 1 1.18 2H15a1 1 0 0 1 0-2ZM3 13v7a1 1 0 0 0 1 1h7v-8H3Zm10 8h7a1 1 0 0 0 1-1v-7h-8v8Z" />
  </svg>
);

function fireFreeGiftConfetti(canvas) {
  if (typeof window === "undefined" || !canvas) return;
  const colors = ["#f87739", "#e25886", "#e2b658", "#58e2af", "#589be2", "#e25858"];
  const fire = confetti.create(canvas, { resize: true, useWorker: true });
  const defaults = { origin: { x: 0.5, y: 0.22 }, colors };
  fire({ ...defaults, particleCount: 55, spread: 58, startVelocity: 38 });
  fire({ ...defaults, particleCount: 35, spread: 85, decay: 0.92, scalar: 0.9 });
  fire({ ...defaults, particleCount: 22, spread: 100, startVelocity: 22, scalar: 1.05 });
}

function getAnnouncementTexts(section) {
  const fromArray = (value) =>
    (Array.isArray(value) ? value : [])
      .map((entry) => String(entry ?? "").trim())
      .filter(Boolean);

  const settingTexts = fromArray(section?.setting?.announcementTexts);
  if (settingTexts.length) return settingTexts;

  const sectionTexts = fromArray(section?.texts);
  if (sectionTexts.length) return sectionTexts;

  const raw = String(section?.text ?? "").trim();
  if (!raw) return [];
  const split = raw
    .split(/\n+| \| /)
    .map((part) => part.trim())
    .filter(Boolean);
  return split.length ? split : [raw];
}

function getAnnouncementRotateMs(section) {
  const raw =
    Number(section?.setting?.announcementRotateMs) ||
    Number(section?.setting?.announcementRotateSeconds) * 1000;
  if (Number.isFinite(raw) && raw >= 1200) return raw;
  return ANNOUNCEMENT_ROTATE_MS;
}

function findRewardProductForLine(item, rewardSetting) {
  const tierId = getLineRewardTierId(item);
  if (!tierId || !rewardSetting) return null;
  const tiers = Array.isArray(rewardSetting.tiers) ? rewardSetting.tiers : [];
  const tier = tiers.find((entry) => String(entry?.id) === String(tierId));
  if (!tier || tier.rewardType !== REWARD_TIER_TYPE.PRODUCT) return null;

  // Prefer resolved gift pool (collection / all_except), then static tier.products.
  const products = getRewardProductsForTier(tier);

  const lineVariantId = String(item?.variant_id || "");
  const lineProductId = extractShopifyNumericId(
    item?.product_id || item?.productId || ""
  );

  const match =
    products.find((product) => {
      const variantId = resolveRewardProductVariantId(product, tier);
      if (variantId && lineVariantId && String(variantId) === lineVariantId) {
        return true;
      }
      const productId = extractShopifyNumericId(product?.id);
      return Boolean(productId && lineProductId && productId === lineProductId);
    }) || null;

  return { tier, product: match };
}

function isRewardTierAchieved(tierId, rewardSetting, cart) {
  if (!tierId || !rewardSetting || !cart) return false;
  const state = computeRewardBarState(
    cart,
    rewardSetting,
    undefined,
    getRewardBarMatchOptions()
  );
  return [...(state.achievedTierIds || [])].some(
    (id) => String(id) === String(tierId)
  );
}

function getRewardProductImageUrl(product) {
  if (!product || typeof product !== "object") return "";
  const candidates = [
    product.featured_image,
    product.featuredImage,
    product.image,
    product.images?.[0],
    product.media?.[0]?.preview?.image,
    product.media?.[0]?.image,
    product.variants?.[0]?.featured_image,
    product.variants?.[0]?.image,
  ];
  for (const entry of candidates) {
    if (!entry) continue;
    if (typeof entry === "string" && entry.trim()) return entry.trim();
    const src = entry.src || entry.url || entry.originalSrc || entry.original_src;
    if (typeof src === "string" && src.trim()) return src.trim();
  }
  return "";
}

/**
 * Free-gift chrome only when:
 * - line is tagged to a product reward tier
 * - that tier is currently achieved
 * - line variant matches a currently selected reward product
 * - tier.isFreeGift is checked (explicit merchant setting)
 */
function isFreeGiftRewardLine(item, rewardSetting, cart) {
  const tierId = getLineRewardTierId(item);
  if (!tierId) return false;
  if (!isRewardTierAchieved(tierId, rewardSetting, cart)) return false;

  const matched = findRewardProductForLine(item, rewardSetting);
  if (
    !matched?.tier ||
    matched.tier.rewardType !== REWARD_TIER_TYPE.PRODUCT ||
    !matched.product
  ) {
    return false;
  }
  return matched.tier.isFreeGift !== false;
}

/**
 * Hide reward-product lines from the drawer until their tier is unlocked.
 * Does not change cart grant/remove logic — display only.
 */
function shouldHideUnachievedRewardProduct(item, rewardSetting, cart) {
  const tierId = getLineRewardTierId(item);
  if (!tierId) return false;
  const matched = findRewardProductForLine(item, rewardSetting);
  if (!matched?.tier) return false;
  return !isRewardTierAchieved(tierId, rewardSetting, cart);
}

/**
 * Hide stale reward-tagged lines that no longer match the selected product(s).
 * (e.g. old "Free surprise gift" placeholder still in cart after product change)
 */
function shouldHideOrphanedRewardProduct(item, rewardSetting) {
  const tierId = getLineRewardTierId(item);
  if (!tierId || !rewardSetting) return false;

  const tiers = Array.isArray(rewardSetting.tiers) ? rewardSetting.tiers : [];
  const tier = tiers.find((entry) => String(entry?.id) === String(tierId));
  if (!tier || tier.rewardType !== REWARD_TIER_TYPE.PRODUCT) return false;

  const products = getRewardProductsForTier(tier);
  // Dynamic pools may still be loading — don't hide reward lines as orphans.
  if (!products.length) {
    const offerType = tier?.products_to_offer?.type || "specific_product";
    if (offerType !== "specific_product") return false;
    return true;
  }

  const activeVariantIds = new Set(
    products
      .map((product) => resolveRewardProductVariantId(product, tier))
      .filter(Boolean)
      .map(String)
  );
  if (!activeVariantIds.size) return true;
  return !activeVariantIds.has(String(item?.variant_id || ""));
}

/** Cart line thumb with shimmer until CDN image is ready (avoids blank white box). */
function CartLineThumb({ item, fallbackSrc = "", showGiftPlaceholder = false }) {
  const resolved = getCartLineItemThumbUrl(item, CART_LINE_THUMB_CDN_WIDTH);
  const [useFallback, setUseFallback] = useState(!resolved && Boolean(fallbackSrc));
  const src = (useFallback ? fallbackSrc : resolved) || fallbackSrc || "";
  const thumbKey = `${item?.key || item?.variant_id || ""}:${src}`;
  const [status, setStatus] = useState(src ? "loading" : "error");

  useEffect(() => {
    setUseFallback(!resolved && Boolean(fallbackSrc));
    setStatus(resolved || fallbackSrc ? "loading" : "error");
  }, [resolved, fallbackSrc, thumbKey]);

  const bindImg = useCallback((node) => {
    if (!node) return;
    if (node.complete && node.naturalWidth > 0) {
      setStatus("loaded");
    }
  }, []);

  return (
    <>
      {status === "loading" ? (
        <span className="weu-custom-cart-drawer__thumb-shimmer" aria-hidden />
      ) : null}
      {src && status !== "error" ? (
        <img
          key={thumbKey}
          ref={bindImg}
          src={src}
          alt={item.product_title || item.title || ""}
          className={`weu-custom-cart-drawer__thumb${
            status === "loaded" ? " weu-custom-cart-drawer__thumb--loaded" : ""
          }`}
          width={CART_LINE_THUMB_DISPLAY_PX}
          height={CART_LINE_THUMB_DISPLAY_PX}
          loading="eager"
          decoding="async"
          fetchPriority="high"
          onLoad={() => setStatus("loaded")}
          onError={() => {
            if (!useFallback && fallbackSrc && src !== fallbackSrc) {
              setUseFallback(true);
              setStatus("loading");
              return;
            }
            setStatus("error");
          }}
        />
      ) : null}
      {status === "error" && showGiftPlaceholder ? (
        <span
          className="weu-custom-cart-drawer__thumb-gift-placeholder"
          aria-hidden
        >
          <GiftBadgeIcon />
        </span>
      ) : null}
    </>
  );
}

function resolveCartCurrencyCode(cart, fallback) {
  if (cart?.currency) return cart.currency;
  if (typeof window === "undefined") return fallback || "USD";
  return (
    window.Shopify?.currency?.active ||
    window.Shopify?.currency?.currencyCode ||
    fallback ||
    "USD"
  );
}

function toFontSizeCSSValue(size) {
  if (size == null || size === "") return undefined;
  if (typeof size === "number") return `${size}px`;
  const trimmed = String(size).trim();
  if (/^\d+$/.test(trimmed)) return `${trimmed}px`;
  return trimmed;
}

function resolveLineDividerColor(bodySetting, layoutBody, titleColor) {
  const custom =
    bodySetting?.lineDividerColor ??
    bodySetting?.lineItemBorderColor ??
    layoutBody?.lineItemBorderColor;
  if (custom != null && String(custom).trim() !== "") return custom;
  if (titleColor) return titleColor;
  return "#e8ecf0";
}

/**
 * Shopify /cart/change.js can swap sibling-variant images onto the product
 * featured image after remove/qty. Keep prior line media by cart line key.
 * Also keep enriched compare-at fields across cart.js refreshes.
 */
function mergeCartLineMediaFromPrevious(previousCart, nextCart) {
  if (!nextCart || typeof nextCart !== "object") return nextCart;
  const prevItems = Array.isArray(previousCart?.items) ? previousCart.items : [];
  const nextItems = Array.isArray(nextCart.items) ? nextCart.items : [];
  if (!nextItems.length) return nextCart;

  const prevByKey = new Map();
  const prevByVariant = new Map();
  for (const item of prevItems) {
    const key = String(item?.key || "");
    const variantId = String(item?.variant_id || "");
    if (key) prevByKey.set(key, item);
    if (variantId) prevByVariant.set(variantId, item);
  }

  return {
    ...nextCart,
    items: nextItems.map((item) => {
      const key = String(item?.key || "");
      const variantId = String(item?.variant_id || "");
      const prev =
        (key && prevByKey.get(key)) ||
        (variantId && prevByVariant.get(variantId)) ||
        null;

      let next = item;
      if (prev) {
        const prevImage =
          prev.image || prev.featured_image?.url || prev.featured_image?.src;
        const nextImage =
          item.image || item.featured_image?.url || item.featured_image?.src;
        if (prevImage && nextImage && prevImage !== nextImage) {
          next = {
            ...next,
            image: prev.image || item.image,
            featured_image: prev.featured_image || item.featured_image,
          };
        }
        const prevCompare = Number(prev._weu_compare_at_price) || 0;
        if (prevCompare > 0 && !(Number(next._weu_compare_at_price) > 0)) {
          const qty = Math.max(1, Number(next.quantity) || 1);
          next = {
            ...next,
            _weu_compare_at_price: prevCompare,
            _weu_compare_at_line: prevCompare * qty,
          };
        }
      }
      return next;
    }),
  };
}

const EDITOR_EMPTY_PREVIEW_CART = {
  items: [],
  item_count: 0,
  total_price: 0,
  items_subtotal_price: 0,
  original_total_price: 0,
  total_discount: 0,
  currency: "USD",
};

const EDITOR_PREVIEW_VARIANT_OPTIONS = {
  "preview-line-1": [
    {
      id: "10011",
      label: "Charcoal / M",
      available: true,
      image:
        "https://cdn.shopify.com/s/files/1/0533/2089/files/placeholder-images-product-1_large.png",
    },
    {
      id: "10012",
      label: "White / M",
      available: true,
      image:
        "https://cdn.shopify.com/s/files/1/0533/2089/files/placeholder-images-product-2_large.png",
    },
    {
      id: "10013",
      label: "Red / M",
      available: false,
      image:
        "https://cdn.shopify.com/s/files/1/0533/2089/files/placeholder-images-product-3_large.png",
    },
  ],
  "preview-line-2": [
    {
      id: "10021",
      label: "Light Blue",
      available: true,
      image:
        "https://cdn.shopify.com/s/files/1/0533/2089/files/placeholder-images-product-2_large.png",
    },
    {
      id: "10022",
      label: "White",
      available: false,
      image:
        "https://cdn.shopify.com/s/files/1/0533/2089/files/placeholder-images-product-1_large.png",
    },
  ],
};

const EDITOR_PREVIEW_CART = {
  items: [
    {
      key: "preview-line-1",
      variant_id: 10011,
      handle: "classic-t-shirt",
      product_id: 1001,
      product_title: "Classic T-Shirt",
      product_type: "Apparel",
      vendor: "WeUpsell Demo",
      tags: "cotton,tee,classic",
      variant_title: "Charcoal / M",
      options_with_values: [
        { name: "Color", value: "Charcoal" },
        { name: "Size", value: "M" },
      ],
      quantity: 2,
      original_line_price: 9998,
      final_line_price: 4998,
      line_price: 4998,
      compare_at_price: 6499,
      _weu_compare_at_price: 6499,
      _weu_compare_at_line: 12998,
      line_level_discount_allocations: [{ amount: 5000 }],
      image:
        "https://cdn.shopify.com/s/files/1/0533/2089/files/placeholder-images-product-1_large.png",
      selling_plan_allocation: {
        selling_plan: {
          id: 14344585510,
          name: "Deliver every week, 23% off",
        },
      },
    },
    {
      key: "preview-line-2",
      variant_id: 10021,
      handle: "classic-t-shirt-light",
      product_id: 1002,
      product_title: "Classic T-Shirt",
      product_type: "Apparel",
      vendor: "WeUpsell Demo",
      tags: "cotton,tee,lightweight",
      variant_title: "Light Blue",
      options_with_values: [{ name: "Color", value: "Light Blue" }],
      quantity: 1,
      original_line_price: 2499,
      final_line_price: 2499,
      line_price: 2499,
      compare_at_price: 3499,
      _weu_compare_at_price: 3499,
      _weu_compare_at_line: 3499,
      image:
        "https://cdn.shopify.com/s/files/1/0533/2089/files/placeholder-images-product-2_large.png",
    },
  ],
  item_count: 3,
  original_total_price: 10498,
  total_price: 7497,
  total_discount: 3001,
  items_subtotal_price: 7497,
  cart_level_discount_applications: [
    {
      type: "discount_code",
      title: "10% Discount",
      value: "10.0",
      value_type: "percentage",
      total_allocated_amount: 3001,
    },
  ],
  discount_codes: [{ code: "10OFF", applicable: true }],
  currency: "USD",
};

const DEFAULT_VARIANT_LABELS = new Set([
  "default title",
  "default",
  "default variant",
]);

function isDefaultVariantValue(value) {
  if (value == null) return true;
  const normalized = String(value).trim().toLowerCase();
  return !normalized || DEFAULT_VARIANT_LABELS.has(normalized);
}

function getSellingPlanLabel(item) {
  const plan = item?.selling_plan_allocation?.selling_plan;
  if (!plan) return null;
  const name = String(plan.name || "").trim();
  if (name) return name;
  const description = String(plan.description || "").trim();
  return description || null;
}

function getVariantLabel(item, separator = " � ") {
  if (Array.isArray(item?.options_with_values) && item.options_with_values.length) {
    const parts = item.options_with_values
      .filter((o) => o?.value != null && !isDefaultVariantValue(o.value))
      .map((o) => {
        const name = o?.name?.trim();
        const value = String(o.value).trim();
        return name ? `${name}: ${value}` : value;
      })
      .filter(Boolean);

    if (parts.length) {
      return parts.join(separator);
    }
  }

  const variantTitle = item?.variant_title?.trim();
  if (variantTitle && !isDefaultVariantValue(variantTitle)) {
    return variantTitle;
  }

  return null;
}

function getCartLineProductHandle(item) {
  const handle = String(item?.handle || "").trim();
  if (handle) return handle.replace(/^\/+|\/+$/g, "");
  const url = String(item?.url || "");
  const match = url.match(/\/products\/([^/?#]+)/i);
  if (!match?.[1]) return "";
  try {
    return decodeURIComponent(match[1]);
  } catch {
    return match[1];
  }
}

function buildVariantSelectOptionsFromProduct(product) {
  const variants = Array.isArray(product?.variants) ? product.variants : [];
  if (variants.length < 2) return [];
  return variants
    .filter((variant) => variant?.id != null)
    .map((variant) => {
      const title = String(variant.title || "").trim();
      const label = !isDefaultVariantValue(title) ? title : String(variant.id);
      const featured = variant.featured_image;
      const image =
        (typeof featured === "string" && featured) ||
        featured?.src ||
        "";
      return {
        id: String(variant.id),
        label,
        available: variant.available !== false,
        image,
      };
    });
}

function CartLineVariantRow({
  item,
  lineKey,
  variantLabel,
  variantStyle,
  showVariantSelector,
  variantOptionsByHandle,
  lineBusy,
  controlsEnabled,
  onVariantChange,
  showVariantOutOfStockBadge = true,
  variantOutOfStockBadgeText = "Out of stock",
  variantOutOfStockBadgeStyle = null,
}) {
  if (!item) return null;

  const handle = getCartLineProductHandle(item);
  const variantOptions =
    (handle && variantOptionsByHandle?.[handle]) ||
    EDITOR_PREVIEW_VARIANT_OPTIONS[lineKey] ||
    [];
  const canSelectVariant =
    showVariantSelector && variantOptions.length > 1;

  if (canSelectVariant) {
    const selectedId = String(
      item?.variant_id || variantOptions[0]?.id || ""
    );
    const selectedOption =
      variantOptions.find((option) => String(option.id) === selectedId) ||
      null;
    const selectedUnavailable = selectedOption?.available === false;
    return (
      <CartDrawerVariantSelect
        options={variantOptions}
        value={selectedId}
        disabled={lineBusy || !controlsEnabled}
        style={{
          ...variantStyle,
          ...(selectedUnavailable ? { color: "#9ca3af" } : null),
        }}
        showOutOfStockBadge={showVariantOutOfStockBadge}
        outOfStockBadgeText={variantOutOfStockBadgeText}
        outOfStockBadgeStyle={variantOutOfStockBadgeStyle}
        onChange={(nextId) => onVariantChange?.(item, nextId)}
      />
    );
  }

  if (!variantLabel) return null;
  return (
    <p className="weu-custom-cart-drawer__variant" style={variantStyle}>
      {variantLabel}
    </p>
  );
}

function syncCartContextSnapshot(cart) {
  if (typeof window === "undefined" || !cart || typeof cart !== "object") return;
  window.__weupsellCart = {
    total: cart.total_price || 0,
    itemCount: cart.item_count || 0,
    items: Array.isArray(cart.items) ? cart.items : [],
  };
}

function TermsAgreementLink({ label, href, embeddedInEditor }) {
  if (!label) return null;
  if (!href) return <span>{label}</span>;
  return (
    <a
      className="weu-custom-cart-drawer__terms-link"
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      onClick={(event) => {
        event.stopPropagation();
        if (embeddedInEditor) event.preventDefault();
      }}
    >
      {label}
    </a>
  );
}

const CustomCartDrawer = ({
  open = false,
  onClose,
  popupData,
  layout: layoutProp,
  embeddedInEditor = false,
}) => {
  const { isMobile, appState } = useApp();
  const isAdminPreview = embeddedInEditor || Boolean(appState?.is_preview_mode);

  if (typeof window !== "undefined" && !embeddedInEditor) {
    window.__WEUPSELL_STATIC_CART_DRAWER_OPEN__ = !!open;
  }

  const drawerStepIndex = useMemo(
    () => getStaticCartDrawerSteps(popupData).drawerStepIndex ?? 0,
    [popupData, popupData?.schema?.length]
  );

  const drawerStep = popupData?.schema?.[drawerStepIndex];

  const sections = useMemo(
    () => getStaticCartDrawerSections(popupData, drawerStepIndex),
    [
      popupData,
      drawerStepIndex,
      drawerStep?.section,
      drawerStep?.sections,
      drawerStep?.cartDrawerLayout,
    ]
  );

  const layout = useMemo(
    () => layoutProp || getCustomCartDrawerLayout(popupData),
    [
      layoutProp,
      popupData,
      drawerStepIndex,
      drawerStep?.section,
      drawerStep?.cartDrawerLayout,
    ]
  );

  const designTokens = useMemo(
    () =>
      readCartDrawerDesignTokens(
        drawerStep?.cartDrawerLayout,
        drawerStep?.popupSettings
      ),
    [
      drawerStep?.cartDrawerLayout,
      drawerStep?.cartDrawerLayout?.design,
      drawerStep?.popupSettings,
    ]
  );

  const safeMerchantCss = useMemo(
    () => sanitizeMerchantCustomCss(designTokens.customCss),
    [designTokens.customCss]
  );

  const [selectedPartIndex, setSelectedPartIndex] = useState(
    () =>
      popupData?.generalSettings?.staticCartSelectedPart ??
      STATIC_CART_SECTION_INDEX.HEADER_TITLE
  );
  const [hoveredPartIndex, setHoveredPartIndex] = useState(null);

  useEffect(() => {
    const fromProps = popupData?.generalSettings?.staticCartSelectedPart;
    if (typeof fromProps === "number" && fromProps >= 0) {
      setSelectedPartIndex(fromProps);
    }
  }, [popupData?.generalSettings?.staticCartSelectedPart]);

  useEffect(() => {
    if (!embeddedInEditor) return undefined;

    const onMessage = (event) => {
      const data = event?.data;
      if (!data || typeof data !== "object") return;

      if (isDeselectBlockMessage(data)) {
        setSelectedPartIndex(STATIC_CART_SECTION_INDEX.HEADER_TITLE);
        setHoveredPartIndex(null);
        return;
      }

      const selection = parseSelectBlockMessage(data);
      if (!selection || typeof selection.index !== "number") return;
      if (selection.index < 0 || selection.index >= sections.length) return;
      if (typeof selection.subIndex === "number" && selection.subIndex >= 0) {
        return;
      }

      setSelectedPartIndex(selection.index);
      setHoveredPartIndex(null);
    };

    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, [embeddedInEditor, sections.length]);

  const selectEditorPart = useCallback(
    (index) => {
      if (!embeddedInEditor) return;
      if (index === selectedPartIndex) return;
      const sectionType = sections[index]?.type;
      ackSelectBlockToParent({ type: sectionType, index });
      setSelectedPartIndex(index);
      setHoveredPartIndex(null);
    },
    [embeddedInEditor, sections, selectedPartIndex]
  );

  const getEditorPartProps = useCallback(
    (index, baseStyle) => {
      if (!embeddedInEditor) {
        return baseStyle ? { style: baseStyle } : {};
      }
      const isSelected = selectedPartIndex === index;
      const isHovered = hoveredPartIndex === index && !isSelected;
      return {
        ...editorPartA11yProps(true),
        onMouseEnter: () => {
          if (!isSelected) setHoveredPartIndex(index);
        },
        onMouseLeave: () => {
          setHoveredPartIndex((prev) => (prev === index ? null : prev));
        },
        onClick: (event) => {
          event.stopPropagation();
          selectEditorPart(index);
        },
        onKeyDown: (event) => {
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            event.stopPropagation();
            selectEditorPart(index);
          }
        },
        style: mergeEditorPartStyle(baseStyle, {
          embeddedInEditor: true,
          isSelected,
          isHovered,
        }),
      };
    },
    [embeddedInEditor, hoveredPartIndex, selectEditorPart, selectedPartIndex]
  );

  const titleSection = sections[STATIC_CART_SECTION_INDEX.HEADER_TITLE];
  const closeSection = sections[STATIC_CART_SECTION_INDEX.HEADER_CLOSE];
  const dividerSection = sections[STATIC_CART_SECTION_INDEX.HEADER_DIVIDER];
  const announcementSection =
    sections[STATIC_CART_SECTION_INDEX.ANNOUNCEMENT_BAR];
  const rewardBarSection = sections[STATIC_CART_SECTION_INDEX.REWARD_BAR];
  const bodySection = sections[STATIC_CART_SECTION_INDEX.BODY];
  const upsellHeadingSection =
    sections[STATIC_CART_SECTION_INDEX.UPSELL_HEADING];
  const upsellProductSection =
    sections[STATIC_CART_SECTION_INDEX.UPSELL_PRODUCT];
  const discountCodeSection =
    sections[STATIC_CART_SECTION_INDEX.DISCOUNT_CODE_INPUT];
  const orderNoteSection = sections[STATIC_CART_SECTION_INDEX.ORDER_NOTE];
  const subtotalLabelSection =
    sections[STATIC_CART_SECTION_INDEX.FOOTER_SUBTOTAL_LABEL];
  const subtotalPriceSection =
    sections[STATIC_CART_SECTION_INDEX.FOOTER_SUBTOTAL_PRICE];
  const discountSection = sections[STATIC_CART_SECTION_INDEX.FOOTER_DISCOUNT];
  const taxNoteSection = sections[STATIC_CART_SECTION_INDEX.FOOTER_TAX_NOTE];
  const checkoutSection = sections[STATIC_CART_SECTION_INDEX.FOOTER_CHECKOUT];

  const showHeaderTitle = isSectionVisible(titleSection);
  const showHeaderClose = isSectionVisible(closeSection);
  const showHeaderDivider = isSectionVisible(dividerSection);
  const injectOnlyPreview =
    isAdminPreview && isCustomCartDrawerThemeUpsellInjectMode(popupData);
  const showAnnouncementBar =
    !injectOnlyPreview && isSectionVisible(announcementSection);
  const showRewardBar =
    !injectOnlyPreview && isSectionVisible(rewardBarSection);
  /** When reward bar is hidden, skip all reward discount / gift logic. */
  const activeRewardBarSection = showRewardBar ? rewardBarSection : null;
  const rewardTrackingContext = useMemo(
    () => ({
      shop:
        typeof window !== "undefined"
          ? window?.Shopify?.shop || window.location.hostname
          : "",
      popup: popupData,
    }),
    [popupData]
  );
  const showBody = isSectionVisible(bodySection);
  const showUpsellProduct = isSectionVisible(upsellProductSection);
  const showDiscountCodeInput = isSectionVisible(discountCodeSection);
  const showOrderNote = isSectionVisible(orderNoteSection);
  const showSubtotalLabel = true;
  const showSubtotalPrice = true;
  const showDiscount = isSectionVisible(discountSection);
  const showTaxNote = isSectionVisible(taxNoteSection);
  const showCheckout = isSectionVisible(checkoutSection);

  const titleCss = useMemo(() => {
    const base = getCartDrawerHeaderTitleTypography(titleSection, isMobile);
    return {
      ...base,
      color: base.color || designTokens.primaryText,
    };
  }, [titleSection, isMobile, designTokens.primaryText]);
  const headerSubtitleStyle = useMemo(() => {
    const viewport = isMobile ? "mobile" : "desktop";
    const rawFontFamily =
      titleSection?.setting?.customCSS?.[viewport]?.fontFamily ||
      titleSection?.setting?.customCSS?.desktop?.fontFamily ||
      titleCss.fontFamily;
    const subtitleSize = toFontSizeCSSValue(layout.header?.subtitleFontSize);
    const subtitleColor =
      layout.header?.subtitleColor || designTokens.secondaryText;
    return {
      ...buildFontFamilyStyle(rawFontFamily),
      ...(subtitleSize ? { fontSize: subtitleSize } : null),
      color: subtitleColor,
    };
  }, [
    titleSection,
    isMobile,
    titleCss.fontFamily,
    layout.header?.subtitleFontSize,
    layout.header?.subtitleColor,
    designTokens.secondaryText,
  ]);
  const checkoutLabelText =
    checkoutSection?.text?.trim() ||
    layout.footer?.checkoutLabel ||
    "Secure Checkout";
  const checkoutLeftIconUrl =
    checkoutSection?.setting?.checkoutLeftIcon?.image?.trim() || "";
  const checkoutRightIconUrl =
    checkoutSection?.setting?.checkoutRightIcon?.image?.trim() || "";
  const closeCss = useMemo(
    () => getCartDrawerFlowCss(closeSection, isMobile),
    [closeSection, isMobile]
  );
  const bodyCss = useMemo(
    () => stripContainerSpacing(getCartDrawerFlowCss(bodySection, isMobile)),
    [bodySection, isMobile]
  );
  const bodySetting = bodySection?.setting || {};
  const layoutBody = layout.body || {};
  const showRemoveButton =
    bodySetting.hideDeleteButton === true
      ? false
      : layoutBody.showRemoveButton !== false;
  const showQuantityControls =
    bodySetting.hideQuantityButton === true
      ? false
      : layoutBody.showQuantityControls !== false;
  const showLineComparePrice = bodySetting.showLineComparePrice !== false;
  // Default on so discount names (BUY 1 GET 1, 15% OFF) show under titles.
  // Merchants can still turn this off in cart item settings.
  const showLineDiscountChip = bodySetting.showLineDiscountChip !== false;
  const showVariantSelector = bodySetting.showVariantSelector === true;
  const showVariantOutOfStockBadge =
    layoutBody.showVariantOutOfStockBadge !== false;
  const variantOutOfStockBadgeText =
    String(layoutBody.variantOutOfStockBadgeText || "").trim() ||
    "Out of stock";
  const variantOutOfStockBadgeStyle = {
    color: layoutBody.variantOutOfStockBadgeColor || "#64748b",
    background: layoutBody.variantOutOfStockBadgeBackground || "#f8fafc",
    borderColor: layoutBody.variantOutOfStockBadgeBorderColor || "#e2e8f0",
  };
  const savingsColor =
    bodySetting.savingsBadgeColor ||
    bodySetting.savingsColor ||
    layoutBody.savingsBadgeColor ||
    "#2e7d32";
  const savingsBadgeBackground =
    bodySetting.savingsBadgeBackground ||
    layoutBody.savingsBadgeBackground ||
    "#e6f4ea";
  const useLineItemCards =
    bodySetting.lineItemCard === true || layoutBody.lineItemCard === true;
  const variantSeparator =
    layoutBody.variantSeparator || bodySetting.variantSeparator || " � ";
  const showCheckoutLock =
    checkoutSection?.setting?.showLockIcon !== false &&
    layout.footer?.checkoutShowLockIcon !== false;
  const showCheckoutSparkle =
    checkoutSection?.setting?.showSparkleIcon !== false &&
    layout.footer?.checkoutShowSparkleIcon !== false;
  const termsAgreement = resolveCartDrawerTermsAgreement(
    layout.footer,
    checkoutSection?.setting
  );
  const showTerms = Boolean(showCheckout && termsAgreement.enabled);

  const schemaStep = drawerStep;
  const emptyMessage =
    schemaStep?.cartDrawerLayout?.body?.emptyMessage ||
    layout.body?.emptyMessage ||
    "Your cart is empty";
  const showEmptyCartIcon = layoutBody.showEmptyCartIcon !== false;
  const showContinueShopping = layoutBody.showContinueShopping !== false;
  const continueShoppingLabel =
    layoutBody.continueShoppingLabel || "Continue shopping";
  const continueShoppingAction =
    layoutBody.continueShoppingAction || "collections";
  const continueShoppingUrl =
    layoutBody.continueShoppingUrl || "/collections/all";
  const continueShoppingPadY =
    Number(layoutBody.continueShoppingPaddingY) >= 0
      ? Number(layoutBody.continueShoppingPaddingY)
      : 12;
  const continueShoppingPadX =
    Number(layoutBody.continueShoppingPaddingX) >= 0
      ? Number(layoutBody.continueShoppingPaddingX)
      : 28;
  const continueShoppingMarginTop =
    Number(layoutBody.continueShoppingMarginTop) >= 0
      ? Number(layoutBody.continueShoppingMarginTop)
      : 4;
  const continueShoppingButtonStyle = {
    backgroundColor: layoutBody.continueShoppingButtonBg || "#000000",
    color: layoutBody.continueShoppingButtonColor || "#ffffff",
    fontSize: `${
      Number(layoutBody.continueShoppingFontSize) > 0
        ? Number(layoutBody.continueShoppingFontSize)
        : 14
    }px`,
    borderRadius: `${
      layoutBody.continueShoppingButtonRadius != null &&
      layoutBody.continueShoppingButtonRadius !== ""
        ? parseFloat(layoutBody.continueShoppingButtonRadius) || 0
        : 999
    }px`,
    borderWidth: `${
      Number(layoutBody.continueShoppingBorderWidth) > 0
        ? Number(layoutBody.continueShoppingBorderWidth)
        : 0
    }px`,
    borderStyle: "solid",
    borderColor:
      layoutBody.continueShoppingBorderColor ||
      layoutBody.continueShoppingButtonBg ||
      "#000000",
    padding: `${continueShoppingPadY}px ${continueShoppingPadX}px`,
    marginTop: `${continueShoppingMarginTop}px`,
  };

  const handleContinueShopping = useCallback(() => {
    if (embeddedInEditor) return;
    const action = String(continueShoppingAction || "collections").toLowerCase();
    if (action === "close") {
      onClose?.();
      return;
    }
    const root = getShopifyRoutesRoot();
    let target = root;
    if (action === "collections") {
      target = `${root}collections/all`;
    } else if (action === "custom") {
      const raw = String(continueShoppingUrl || "/").trim() || "/";
      if (/^https?:\/\//i.test(raw)) {
        target = raw;
      } else {
        target = `${root}${raw.replace(/^\//, "")}`;
      }
    }
    onClose?.();
    if (typeof window !== "undefined") {
      window.location.assign(target);
    }
  }, [
    continueShoppingAction,
    continueShoppingUrl,
    embeddedInEditor,
    onClose,
  ]);

  const lineItemTypography = useMemo(() => {
    const css = getCartDrawerFlowCss(bodySection, isMobile);
    const s = bodySetting;
    const titleFont = buildFontFamilyStyle(css.fontFamily);
    const primary = designTokens.primaryText;
    const secondary = designTokens.secondaryText;
    const priceColor = designTokens.priceColor;
    const radiusPx = `${designTokens.cornerRadius}px`;
    const base = {
      ...titleFont,
      fontSize: css.fontSize,
      color: css.color || primary,
      fontWeight: css.fontWeight,
    };
    return {
      title: {
        ...titleFont,
        fontSize:
          toFontSizeCSSValue(s.titleFontSize) ||
          toFontSizeCSSValue(css.fontSize) ||
          "13px",
        fontWeight: css.fontWeight,
        color: s.titleColor || css.color || primary,
      },
      variant: {
        ...titleFont,
        fontSize: toFontSizeCSSValue(s.variantFontSize) || "12px",
        color: s.variantColor || secondary,
        opacity: !s.variantColor && css.color ? 0.72 : undefined,
      },
      comparePrice: {
        ...titleFont,
        fontSize:
          toFontSizeCSSValue(s.comparePriceFontSize) ||
          toFontSizeCSSValue(s.compareAtFontSize) ||
          "12px",
        color: s.comparePriceColor || s.compareAtColor || secondary,
      },
      price: {
        ...titleFont,
        fontSize: toFontSizeCSSValue(s.priceFontSize) || "13px",
        color: s.priceColor || css.color || priceColor,
      },
      quantity: {
        ...titleFont,
        fontSize: toFontSizeCSSValue(s.quantityFontSize) || "12px",
        color: s.quantityColor || css.color || primary,
        background: s.quantityBackground || "#f3f4f6",
        borderWidth:
          s.quantityBorderWidth != null && s.quantityBorderWidth !== ""
            ? Number(s.quantityBorderWidth) || 0
            : 0,
        borderColor: s.quantityBorderColor || "#e5e7eb",
        borderRadius:
          toFontSizeCSSValue(s.quantityBorderRadius) || radiusPx,
      },
      empty: { ...base },
      lineDividerColor: resolveLineDividerColor(s, layoutBody, css.color),
    };
  }, [
    bodySection,
    bodySetting,
    layoutBody,
    isMobile,
    designTokens.primaryText,
    designTokens.secondaryText,
    designTokens.priceColor,
    designTokens.cornerRadius,
  ]);

  const subtotalLabelCss = useMemo(
    () => getCartDrawerFlowCss(subtotalLabelSection, isMobile),
    [subtotalLabelSection, isMobile]
  );
  const subtotalPriceCss = useMemo(
    () => getCartDrawerFlowCss(subtotalPriceSection, isMobile),
    [subtotalPriceSection, isMobile]
  );
  const discountCss = useMemo(
    () => getCartDrawerFlowCss(discountSection, isMobile),
    [discountSection, isMobile]
  );
  const checkoutButtonStyle = useMemo(() => {
    const css = getCartDrawerFlowCss(checkoutSection, isMobile);
    const textColor = css.color || "#c5a370";
    const fontSize = css.fontSize || "16px";
    const fontFamily = css.fontFamily;
    const checkoutFontStyle = buildFontFamilyStyle(fontFamily);
    const fontWeight = css.fontWeight || "700";
    const letterSpacing = css.letterSpacing || "0.02em";
    const backgroundColor = css.backgroundColor || "#0a0e1a";
    const backgroundImage = css.backgroundImage;
    const isDefaultBgColor =
      !backgroundColor ||
      String(backgroundColor).toLowerCase() === "#0a0e1a";
    const isDefaultGradient =
      !backgroundImage ||
      String(backgroundImage).includes("121a26");
    const resolvedBackgroundImage =
      backgroundImage && isDefaultBgColor && isDefaultGradient
        ? backgroundImage
        : backgroundImage && !isDefaultBgColor
          ? "none"
          : backgroundImage || "none";
    const {
      backgroundColor: _bgColor,
      background: _bg,
      backgroundImage: _bgImage,
      color: _color,
      fontSize: _fontSize,
      fontFamily: _fontFamily,
      fontWeight: _fontWeight,
      letterSpacing: _letterSpacing,
      ...rest
    } = css;

    return {
      ...rest,
      "--weu-checkout-text-color": textColor,
      "--weu-checkout-bg-color": backgroundColor,
      ...(resolvedBackgroundImage && resolvedBackgroundImage !== "none"
        ? { "--weu-checkout-bg-image": resolvedBackgroundImage }
        : { "--weu-checkout-bg-image": "none" }),
      "--weu-checkout-font-size": fontSize,
      "--weu-checkout-font-weight": fontWeight,
      "--weu-checkout-letter-spacing": letterSpacing,
      ...buildFontFamilyCssVar("--weu-checkout-font-family", fontFamily),
      color: textColor,
      fontSize,
      ...checkoutFontStyle,
      fontWeight,
      letterSpacing,
    };
  }, [checkoutSection, isMobile]);

  const taxNoteStyle = useMemo(() => {
    const css = getCartDrawerFlowCss(taxNoteSection, isMobile);
    return {
      ...css,
      textAlign: "left",
      width: "100%",
      display: "block",
      marginLeft: 0,
      paddingLeft: 0,
      alignSelf: "flex-start",
      marginBottom: "8px !important",
    };
  }, [taxNoteSection, isMobile]);

  const announcementStyle = useMemo(() => {
    const css = getCartDrawerFlowCss(announcementSection, isMobile);
    const borderWidth = Number(css.borderWidth) || 0;
    const { padding: _pad, minHeight: _minH, height: _h, maxHeight: _maxH, ...rest } =
      css || {};
    return {
      ...rest,
      width: "100%",
      boxSizing: "border-box",
      display: "flex",
      alignItems: "center",
      justifyContent: css.justifyContent || "center",
      textAlign: css.textAlign || "center",
      borderStyle: borderWidth > 0 ? css.borderStyle || "solid" : "none",
      padding: "0 12px",
      height: "28px",
      minHeight: "25px",
      maxHeight: "30px",
      overflow: "hidden",
    };
  }, [announcementSection, isMobile]);

  const discountLabelText =
    discountSection?.text || layout.footer?.discountLabel || "Discounts";
  const taxNoteText =
    taxNoteSection?.text || layout.footer?.taxShippingNote || "";
  const discountSetting = discountSection?.setting || {};
  const footerLayout = layout.footer || {};
  const showSavingsBanner =
    discountSetting.showSavingsBanner !== false &&
    footerLayout.showSavingsBanner !== false;
  const savingsBannerBackground = (() => {
    const raw = String(
      discountSetting.savingsBannerBackground ||
        footerLayout.savingsBannerBackground ||
        "#e6f4ea"
    ).toLowerCase();
    // Migrate previous black / mint defaults to light-green savings badge.
    if (raw === "#000000" || raw === "#000" || raw === "#c8f7e0") {
      return "#e6f4ea";
    }
    return (
      discountSetting.savingsBannerBackground ||
      footerLayout.savingsBannerBackground ||
      "#e6f4ea"
    );
  })();
  const savingsBannerColor = (() => {
    const raw = String(
      discountSetting.savingsBannerColor ||
        footerLayout.savingsBannerColor ||
        "#2e7d32"
    ).toLowerCase();
    // Migrate previous white / near-black text on black banner.
    if (raw === "#ffffff" || raw === "#fff" || raw === "#111111") {
      return "#2e7d32";
    }
    return (
      discountSetting.savingsBannerColor ||
      footerLayout.savingsBannerColor ||
      "#2e7d32"
    );
  })();
  const savingsBannerText =
    discountSetting.savingsBannerText ||
    footerLayout.savingsBannerText ||
    "🎉 You're saving {amount} 🥳 !";
  const estimatedTotalSavedText =
    discountSetting.estimatedTotalSavedText ||
    footerLayout.estimatedTotalSavedText ||
    "You saved {amount}!";
  const billSummaryTitle =
    discountSetting.billSummaryTitle ||
    footerLayout.billSummaryTitle ||
    "Bill Summary";
  const billSubtotalLabel = resolveTotalMrpLabelText(
    discountSetting.billSubtotalLabel,
    discountSetting.totalMrpLabel,
    footerLayout.billSubtotalLabel,
    footerLayout.totalMrpLabel,
    subtotalLabelSection?.text,
    layout.footer?.subtotalLabel
  );
  // Footer trigger + first bill row share the same Total MRP label.
  const subtotalLabelText = billSubtotalLabel;
  const totalMrpLabel = billSubtotalLabel;
  const deliveryFeeLabel =
    discountSetting.deliveryFeeLabel ||
    footerLayout.deliveryFeeLabel ||
    "Delivery fee";
  const deliveryFeeText =
    discountSetting.deliveryFeeText ||
    footerLayout.deliveryFeeText ||
    "To be calculated";
  const mrpDiscountLabel =
    discountSetting.mrpDiscountLabel ||
    footerLayout.mrpDiscountLabel ||
    "Discount on MRP";
  // Empty string = use dynamic discount name / reward Discount message.
  const couponDiscountLabelRaw =
    discountSetting.couponDiscountLabel ?? footerLayout.couponDiscountLabel;
  const couponDiscountLabel =
    typeof couponDiscountLabelRaw === "string"
      ? couponDiscountLabelRaw
      : "Coupon discount";
  const grandTotalLabel =
    discountSetting.grandTotalLabel ||
    footerLayout.grandTotalLabel ||
    "Grand total";
  const discountAmountColor =
    discountSetting.discountAmountColor ||
    footerLayout.discountAmountColor ||
    "#2e7d32";
  const billSummaryTitleSize =
    Number(discountSetting.billSummaryTitleSize) ||
    Number(footerLayout.billSummaryTitleSize) ||
    16;
  const billSummaryTitleColor =
    discountSetting.billSummaryTitleColor ||
    footerLayout.billSummaryTitleColor ||
    "#111827";
  const billSummaryLineSize =
    Number(discountSetting.billSummaryLineSize) ||
    Number(footerLayout.billSummaryLineSize) ||
    14;
  const billSummaryLineColor =
    discountSetting.billSummaryLineColor ||
    footerLayout.billSummaryLineColor ||
    "#111827";
  const billSummaryTitleStyle = {
    fontSize: `${billSummaryTitleSize}px`,
    color: billSummaryTitleColor,
  };
  const billSummaryLineStyle = {
    fontSize: `${billSummaryLineSize}px`,
    color: billSummaryLineColor,
  };
  const billSummaryDetailLayout =
    discountSetting.billSummaryDetailLayout === true ||
    footerLayout.billSummaryDetailLayout === true;
  const billMetaLineSize =
    Number(discountSetting.billMetaLineSize) ||
    Number(footerLayout.billMetaLineSize) ||
    12;
  const sellingSubtotalLabel =
    discountSetting.sellingSubtotalLabel ||
    footerLayout.sellingSubtotalLabel ||
    "Subtotal";
  const billSummaryMetaLineStyle = {
    fontSize: `${billMetaLineSize}px`,
    color: billSummaryLineColor,
  };
  const [billSummaryOpen, setBillSummaryOpen] = useState(false);

  useEffect(() => {
    if (!open) setBillSummaryOpen(false);
  }, [open]);
  const announcementTexts = useMemo(
    () => getAnnouncementTexts(announcementSection),
    [announcementSection, announcementSection?.text, announcementSection?.texts, announcementSection?.setting?.announcementTexts]
  );
  const announcementRotateMs = useMemo(
    () => getAnnouncementRotateMs(announcementSection),
    [announcementSection]
  );
  const [announcementIndex, setAnnouncementIndex] = useState(0);
  const [announcementFading, setAnnouncementFading] = useState(false);

  useEffect(() => {
    setAnnouncementIndex(0);
    setAnnouncementFading(false);
  }, [announcementTexts.join("\n")]);

  useEffect(() => {
    if (announcementTexts.length <= 1) return undefined;
    let fadeTimeoutId = null;
    const intervalId = window.setInterval(() => {
      setAnnouncementFading(true);
      fadeTimeoutId = window.setTimeout(() => {
        setAnnouncementIndex((prev) => (prev + 1) % announcementTexts.length);
        setAnnouncementFading(false);
      }, 320);
    }, announcementRotateMs);
    return () => {
      window.clearInterval(intervalId);
      if (fadeTimeoutId) window.clearTimeout(fadeTimeoutId);
    };
  }, [announcementTexts, announcementRotateMs]);

  const announcementText =
    announcementTexts[
      Math.min(announcementIndex, Math.max(announcementTexts.length - 1, 0))
    ] || "";

  const [cartData, setCartData] = useState(null);
  const pendingOrderNoteRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [pendingCartAdd, setPendingCartAdd] = useState(false);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [releaseHostLayer, setReleaseHostLayer] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(
    () => termsAgreement.preselected
  );
  const [removingDiscount, setRemovingDiscount] = useState(false);
  const [discountCodeLabels, setDiscountCodeLabels] = useState({});
  const upsellFormMethods = useForm({ defaultValues: {}, mode: "onSubmit" });
  const [loadingLines, setLoadingLines] = useState([]);
  /** handle -> selectable variant options (from /products/{handle}.js) */
  const [variantOptionsByHandle, setVariantOptionsByHandle] = useState({});
  const cartDataRef = useRef(null);
  /** Sync guard — React state alone can miss double-clicks before re-render. */
  const loadingLinesRef = useRef(new Set());
  const openedForPendingAddRef = useRef(false);
  const confettiCanvasRef = useRef(null);
  const variantHydrateGenRef = useRef(0);

  useEffect(() => {
    cartDataRef.current = cartData;
  }, [cartData]);

  const fetchCart = useCallback(async ({ silent = false } = {}) => {
    if (isAdminPreview) {
      setLoading(false);
      setPendingCartAdd(false);
      if (typeof window !== "undefined") {
        window.__WEUPSELL_CART_ADD_PENDING__ = false;
      }
      return EDITOR_PREVIEW_CART;
    }

    if (!silent) {
      setLoading(true);
    }
    try {
      const res = await fetch(getShopifyCartUrl("cart.js"));
      if (!res.ok) throw new Error("cart fetch failed");
      const data = await res.json();
      const merged = applyCachedCompareAtPrices(
        mergeCartLineMediaFromPrevious(cartDataRef.current, data)
      );
      seedCartWriteSnapshot(merged);
      // Kick off sized thumbs before paint so React <img> hits memory/disk cache.
      preloadCartLineThumbImages(merged, CART_LINE_THUMB_CDN_WIDTH);
      syncCartContextSnapshot(merged);
      setCartData(merged);
      // Catalog compare-at is often missing from cart.js — enrich from products/{handle}.js
      const enriched = await enrichCartWithCompareAtPrices(merged);
      if (enriched && enriched !== merged) {
        setCartData((current) => {
          const patched = mergeCompareAtOntoCart(current || merged, enriched);
          syncCartContextSnapshot(patched);
          return patched;
        });
        return mergeCompareAtOntoCart(merged, enriched);
      }
      return merged;
    } catch (err) {
      console.error("[CustomCartDrawer] Error fetching cart:", err);
      const emptyCart = { items: [], item_count: 0, total_price: 0 };
      syncCartContextSnapshot(emptyCart);
      setCartData(emptyCart);
      return emptyCart;
    } finally {
      // Always clear pending/loading after cart.js — silent refresh after add.js
      // previously left pendingCartAdd stuck and the body spinner never dismissed.
      setLoading(false);
      setPendingCartAdd(false);
      if (typeof window !== "undefined") {
        window.__WEUPSELL_CART_ADD_PENDING__ = false;
      }
    }
  }, [isAdminPreview]);

  const applyCartSnapshot = useCallback((cart) => {
    if (!cart || typeof cart !== "object") return;
    const merged = applyCachedCompareAtPrices(
      mergeCartLineMediaFromPrevious(cartDataRef.current, cart)
    );
    // Keep change.js key cache aligned after upsell ATC / theme cart sync.
    seedCartWriteSnapshot(merged);
    preloadCartLineThumbImages(merged, CART_LINE_THUMB_CDN_WIDTH);
    syncCartContextSnapshot(merged);
    setCartData(merged);
    pendingOrderNoteRef.current = merged.note || "";
    setLoading(false);
    setPendingCartAdd(false);
    if (typeof window !== "undefined") {
      window.__WEUPSELL_CART_ADD_PENDING__ = false;
    }
    enrichCartWithCompareAtPrices(merged).then((enriched) => {
      if (!enriched || enriched === merged) return;
      // Patch onto whatever cart is current — don't drop enrich if another update won the race
      setCartData((current) => {
        const patched = mergeCompareAtOntoCart(current || merged, enriched);
        syncCartContextSnapshot(patched);
        return patched;
      });
    });
  }, []);

  useEffect(() => {
    const onRefresh = () => {
      fetchCart({ silent: cartDataRef.current != null });
    };
    const onCartUpdated = (event) => {
      if (event?.detail?.cart) {
        applyCartSnapshot(event.detail.cart);
        return;
      }
      fetchCart({ silent: true });
    };
    const onAddFailed = () => {
      setPendingCartAdd(false);
      setLoading(false);
      if (typeof window !== "undefined") {
        window.__WEUPSELL_CART_ADD_PENDING__ = false;
      }
      // Failed add (e.g. max quantity) — close drawer only if this open was for the add.
      if (openedForPendingAddRef.current && open && !embeddedInEditor) {
        openedForPendingAddRef.current = false;
        onClose?.();
      }
    };
    const onOpen = (event) => {
      if (event?.detail?.pendingCartAdd) {
        openedForPendingAddRef.current = true;
        setPendingCartAdd(true);
        setLoading(true);
      }
    };

    window.addEventListener("weupsell:static-cart-drawer-refresh", onRefresh);
    window.addEventListener(
      "weupsell:static-cart-drawer-cart-updated",
      onCartUpdated
    );
    window.addEventListener("weupsell:static-cart-drawer-add-failed", onAddFailed);
    window.addEventListener("weupsell:open-static-cart-drawer", onOpen);

    return () => {
      window.removeEventListener("weupsell:static-cart-drawer-refresh", onRefresh);
      window.removeEventListener(
        "weupsell:static-cart-drawer-cart-updated",
        onCartUpdated
      );
      window.removeEventListener("weupsell:static-cart-drawer-add-failed", onAddFailed);
      window.removeEventListener("weupsell:open-static-cart-drawer", onOpen);
    };
  }, [applyCartSnapshot, embeddedInEditor, fetchCart, onClose, open]);

  useEffect(() => {
    if (!open && !embeddedInEditor) return;

    if (isAdminPreview) {
      setLoading(false);
      setPendingCartAdd(false);
      return;
    }

    const isPending =
      pendingCartAdd ||
      (typeof window !== "undefined" && window.__WEUPSELL_CART_ADD_PENDING__);

    // While add.js is in-flight, keep the drawer in loading state and do not
    // fetch /cart.js early — an empty cart snapshot would flash "Your cart is empty".
    if (open && !embeddedInEditor && isPending) {
      setPendingCartAdd(true);
      setLoading(true);
      return;
    }

    if (open || embeddedInEditor) {
      fetchCart({
        silent: !embeddedInEditor && cartDataRef.current != null,
      });
    }
  }, [open, embeddedInEditor, isAdminPreview, pendingCartAdd, fetchCart]);

  useEffect(() => {
    if (typeof window === "undefined" || embeddedInEditor) return undefined;
    window.__WEUPSELL_STATIC_CART_DRAWER_OPEN__ = !!open;
    if (!open) {
      openedForPendingAddRef.current = false;
      // Theme CartDrawer overlay can stay with .is-active after ATC takeover.
      // Clear it when our drawer closes so the page is not left dimmed.
      try {
        document
          .querySelectorAll(
            '.window-overlay, [data-js-window="overlay"], [data-js-overlay="CartDrawer"]'
          )
          .forEach((el) => {
            el.classList.remove("is-active", "active", "is-open", "open");
            el.remove();
          });
        window.themeCore?.EventBus?.emit?.("Toggle:CartDrawer:close");
        window.themeCore?.EventBus?.emit?.("Overlay:CartDrawer:close", {
          selector: "CartDrawer",
          target: document.getElementById("CartDrawer"),
        });
      } catch (_) {
        /* theme overlay cleanup is best-effort */
      }
    }
    // Hide Smile.io / chat floaters without reparenting the shadow host.
    setThirdPartyFloatersHiddenForCartDrawer(!!open);
    return () => {
      window.__WEUPSELL_STATIC_CART_DRAWER_OPEN__ = false;
      setThirdPartyFloatersHiddenForCartDrawer(false);
    };
  }, [open, embeddedInEditor]);

  useEffect(() => {
    if (embeddedInEditor || !open || typeof window === "undefined") return undefined;
    // Only bump z-index / display — never reparent the shadow host (that
    // reloads shadow CSS and flashes an unstyled drawer on the left).
    promoteWeupsellShadowHostStack();
  }, [open, embeddedInEditor]);

  useEffect(() => {
    if (!pendingCartAdd) return;
    // Successful cart refresh clears pending; keep the flag synced.
    if (cartData != null) {
      openedForPendingAddRef.current = false;
    }
  }, [pendingCartAdd, cartData]);

  useEffect(() => {
    if (open && !embeddedInEditor && (loading || pendingCartAdd)) {
      window.dispatchEvent(new CustomEvent("weupsell:static-cart-drawer-visible"));
    }
  }, [open, loading, pendingCartAdd, embeddedInEditor]);

  const updateQuantity = async (item, quantity) => {
    if (isAdminPreview) return;
    const lineKey = item?.key || item?.id;
    if (!lineKey) {
      console.warn("[CustomCartDrawer] Missing cart line key for quantity update");
      return;
    }
    // Ignore double-clicks on the same line while its change is in flight.
    // Other lines still queue through cart.service (avoids Shopify change races).
    if (loadingLinesRef.current.has(String(lineKey))) return;
    loadingLinesRef.current.add(String(lineKey));
    setLoadingLines((prev) =>
      prev.includes(lineKey) ? prev : [...prev, lineKey]
    );
    try {
      // Prefer the freshest key from cartDataRef — reward sync / prior removes
      // can leave React row props briefly stale while the Ajax cart moved on.
      const liveItem =
        (cartDataRef.current?.items || []).find((row) => {
          const rowKey = String(row?.key || "");
          const want = String(lineKey);
          if (rowKey && rowKey === want) return true;
          if (String(row?.id || "") === want) return true;
          if (item?.key && rowKey === String(item.key)) return true;
          return false;
        }) || item;
      const liveKey = liveItem?.key || liveItem?.id || lineKey;
      const liveStillInCart = (cartDataRef.current?.items || []).some(
        (row) =>
          String(row?.key || "") === String(liveKey) ||
          String(row?.id || "") === String(liveKey)
      );
      if (quantity === 0 && !liveStillInCart) {
        return;
      }

      let nextCart = await cartService.changeLineItem({
        id: liveKey,
        quantity,
        // Helps resolve the live key after order discounts rewrite item.key.
        variantId: liveItem?.variant_id || item?.variant_id,
        properties: liveItem?.properties || item?.properties,
        // Drawer owns UI — skip Liquid section HTML on change.js (major latency).
        sections: false,
        syncCartUi: true,
      });

      // Paint the line change immediately so remove/qty feels instant.
      if (nextCart) {
        applyCartSnapshot(
          mergeCartLineMediaFromPrevious(cartDataRef.current, nextCart)
        );
      }

      // Reward gift sync can take multiple cart writes — don't block the
      // line spinner. Write queue still serializes Shopify changes safely.
      if (activeRewardBarSection?.setting && nextCart) {
        syncRewardProductCartLines(
          nextCart,
          activeRewardBarSection.setting,
          rewardTrackingContext
        )
          .then((syncedCart) => {
            if (!syncedCart) return;
            applyCartSnapshot(
              mergeCartLineMediaFromPrevious(cartDataRef.current, syncedCart)
            );
          })
          .catch((syncErr) => {
            console.warn(
              "[CustomCartDrawer] Reward product sync after qty change failed:",
              syncErr
            );
          });
      }
    } catch (err) {
      const msg = String(err?.message || err || "").toLowerCase();
      const recoverableRemove =
        quantity === 0 &&
        (err?.recoverableCartChange ||
          msg.includes("no valid id") ||
          msg.includes("line parameter") ||
          msg.includes("line item isn't available") ||
          msg.includes("line item is not available"));
      if (recoverableRemove) {
        // Upsell/"You might also like" + rapid remove races — line already gone.
        fetchCart({ silent: true }).catch(() => {});
        return;
      }
      console.error("[CustomCartDrawer] Error updating quantity:", err);
      showCartAddError(err);
    } finally {
      loadingLinesRef.current.delete(String(lineKey));
      setLoadingLines((prev) => prev.filter((l) => l !== lineKey));
    }
  };

  const changeLineVariant = async (item, nextVariantId) => {
    const lineKey = item?.key || item?.id;
    const currentId = String(item?.variant_id || "");
    const nextId = String(nextVariantId || "");
    if (!lineKey || !nextId || nextId === currentId) return;

    const handle = getCartLineProductHandle(item);
    const availabilityOptions =
      (handle && variantOptionsByHandle?.[handle]) ||
      EDITOR_PREVIEW_VARIANT_OPTIONS[lineKey] ||
      [];
    const nextOption = availabilityOptions.find(
      (entry) => String(entry.id) === nextId
    );
    if (nextOption && nextOption.available === false) return;

    if (isAdminPreview) {
      const options =
        EDITOR_PREVIEW_VARIANT_OPTIONS[lineKey] ||
        variantOptionsByHandle[getCartLineProductHandle(item)] ||
        [];
      const selected = options.find((entry) => entry.id === nextId);
      setCartData((prev) => {
        const base = prev || EDITOR_PREVIEW_CART;
        const items = (base.items || []).map((line) => {
          if ((line?.key || line?.id) !== lineKey) return line;
          return {
            ...line,
            variant_id: Number(nextId) || nextId,
            variant_title: selected?.label || line.variant_title,
            options_with_values: selected?.label
              ? [{ name: "Variant", value: selected.label }]
              : line.options_with_values,
            image: selected?.image || line.image,
            featured_image: selected?.image
              ? { url: selected.image, src: selected.image }
              : line.featured_image,
          };
        });
        return { ...base, items };
      });
      return;
    }

    setLoadingLines((prev) => [...prev, lineKey]);
    try {
      let nextCart = await cartService.changeLineItemVariant({
        lineKey,
        variantId: nextId,
        quantity: item?.quantity,
        properties: item?.properties,
        sellingPlanId: item?.selling_plan_allocation?.selling_plan?.id,
      });

      if (nextCart) {
        applyCartSnapshot(
          mergeCartLineMediaFromPrevious(cartDataRef.current, nextCart)
        );
      }

      if (activeRewardBarSection?.setting && nextCart) {
        syncRewardProductCartLines(
          nextCart,
          activeRewardBarSection.setting,
          rewardTrackingContext
        )
          .then((syncedCart) => {
            if (!syncedCart) return;
            applyCartSnapshot(
              mergeCartLineMediaFromPrevious(cartDataRef.current, syncedCart)
            );
          })
          .catch((syncErr) => {
            console.warn(
              "[CustomCartDrawer] Reward product sync after variant change failed:",
              syncErr
            );
          });
      }
    } catch (err) {
      console.error("[CustomCartDrawer] Error changing line variant:", err);
      showCartAddError(err);
      try {
        const refreshed = await fetchCart({ silent: true });
        if (refreshed) {
          applyCartSnapshot(
            mergeCartLineMediaFromPrevious(cartDataRef.current, refreshed)
          );
        }
      } catch {
        /* ignore refresh failure */
      }
    } finally {
      setLoadingLines((prev) => prev.filter((l) => l !== lineKey));
    }
  };

  const goToShopifyCheckout = (discountCodes = []) => {
    const root = getShopifyRoutesRoot();
    let url = `${root}checkout`;
    const codes = (discountCodes || []).filter(Boolean);
    if (codes.length) {
      url += `?${new URLSearchParams({ discount: codes.join(",") }).toString()}`;
    }
    window.location.assign(url);
  };

  const handleRemoveDiscount = useCallback(async () => {
    if (embeddedInEditor || removingDiscount) return;
    setRemovingDiscount(true);
    try {
      const updated = await cartService.clearCartDiscount();
      let freshCart = null;
      try {
        freshCart = await cartService.fetchCart();
      } catch (fetchErr) {
        console.warn("[CustomCartDrawer] cart refresh failed:", fetchErr);
      }
      if (freshCart || updated) {
        applyCartSnapshot(freshCart || updated);
      }
      setDiscountCodeLabels({});
    } catch (err) {
      console.error("[CustomCartDrawer] Failed to remove discount:", err);
    } finally {
      setRemovingDiscount(false);
    }
  }, [applyCartSnapshot, embeddedInEditor, removingDiscount]);

  const handleDiscountApplied = useCallback(({ code, title }) => {
    const key = String(code || "").trim().toLowerCase();
    if (!key) return;
    const nextTitle = title || code;
    setDiscountCodeLabels((prev) => {
      if (prev[key] === nextTitle) return prev;
      return {
        ...prev,
        [key]: nextTitle,
      };
    });
  }, []);

  useEffect(() => {
    if (!hasAppliedCartDiscountCode(cartData)) {
      setDiscountCodeLabels({});
    }
  }, [cartData]);

  useEffect(() => {
    setTermsAccepted(termsAgreement.preselected);
  }, [termsAgreement.enabled, termsAgreement.preselected]);

  const handleCheckout = async () => {
    if (embeddedInEditor || checkoutLoading) return;
    if (!cartData?.items?.length) return;
    if (showTerms && !termsAccepted) return;

    setCheckoutLoading(true);

    try {
      let checkoutCart = cartData;
      if (activeRewardBarSection?.setting) {
        const syncedCart = await syncRewardProductCartLines(
          cartData,
          activeRewardBarSection.setting,
          rewardTrackingContext
        );
        if (syncedCart) {
          applyCartSnapshot(syncedCart);
          checkoutCart = syncedCart;
        }
      }
      const noteToSave = String(
        pendingOrderNoteRef.current ?? checkoutCart?.note ?? ""
      );
      const termsAttributes = withTermsAcceptedAttribute(
        checkoutCart?.attributes,
        showTerms && termsAccepted
      );
      try {
        const noteCart = await cartService.updateCart({
          note: noteToSave,
          ...(termsAttributes ? { attributes: termsAttributes } : {}),
        });
        if (noteCart) {
          applyCartSnapshot(noteCart);
          checkoutCart = noteCart;
          pendingOrderNoteRef.current = noteCart.note || noteToSave;
        }
      } catch (err) {
        console.warn(
          "[CustomCartDrawer] Failed to save order note or terms acceptance:",
          err
        );
      }

      const action = checkoutSection?.setting?.action || { type: "checkout" };
      if (action.type === "open_link") {
        const link = String(action.parameters?.link || action.url || "").trim();
        if (link) {
          if (action.new_tab) {
            window.open(link, "_blank", "noopener,noreferrer");
          } else {
            window.location.assign(link);
          }
          return;
        }
      }

      const discountCodes = extractApplicableCartDiscountCodes(checkoutCart);

      const checkoutDestination = normalizeCheckoutDestinationAction(
        action?.parameters?.checkoutAction
      );

      if (checkoutDestination === BUY_NOW_ACTION_SHIPROCKET) {
        if (tryOpenShiprocketCheckout()) {
          setReleaseHostLayer(true);
          return;
        }
        goToShopifyCheckout(discountCodes);
        return;
      }

      if (checkoutDestination === BUY_NOW_ACTION_GOKWIK) {
        if (tryOpenGokwikCheckout(undefined, { preferBuyNow: false })) {
          setReleaseHostLayer(true);
          return;
        }
        goToShopifyCheckout(discountCodes);
        return;
      }

      if (checkoutDestination === BUY_NOW_ACTION_BREEZE) {
        if (tryOpenBreezeCheckout()) {
          setReleaseHostLayer(true);
          return;
        }
        goToShopifyCheckout(discountCodes);
        return;
      }

      if (checkoutDestination === BUY_NOW_ACTION_RAZORPAY) {
        // Trigger theme checkout control so Razorpay Magic Checkout can open.
        if (tryClickThemeCheckoutButton()) {
          setReleaseHostLayer(true);
          return;
        }
        goToShopifyCheckout(discountCodes);
        return;
      }

      // Default: theme Ajax cart session (/checkout) — Online Store channel.
      goToShopifyCheckout(discountCodes);
    } finally {
      setCheckoutLoading(false);
    }
  };

  const useEditorPreviewData = isAdminPreview;
  const previewEmptyCart =
    useEditorPreviewData &&
    popupData?.generalSettings?.staticCartPreviewEmpty === true;
  const displayCart = useEditorPreviewData
    ? previewEmptyCart
      ? EDITOR_EMPTY_PREVIEW_CART
      : cartData || EDITOR_PREVIEW_CART
    : cartData;
  const displayItems = useMemo(() => {
    const items = Array.isArray(displayCart?.items) ? displayCart.items : [];
    const rewardSetting = activeRewardBarSection?.setting;
    const withIndex = items
      .map((item, index) => ({
        item,
        cartLineIndex: index + 1,
      }))
      .filter(
        ({ item }) =>
          !shouldHideUnachievedRewardProduct(item, rewardSetting, displayCart) &&
          !shouldHideOrphanedRewardProduct(item, rewardSetting)
      );
    withIndex.sort((a, b) => {
      const aGift = isFreeGiftRewardLine(a.item, rewardSetting, displayCart)
        ? 0
        : 1;
      const bGift = isFreeGiftRewardLine(b.item, rewardSetting, displayCart)
        ? 0
        : 1;
      if (aGift !== bGift) return aGift - bGift;
      return a.cartLineIndex - b.cartLineIndex;
    });
    return withIndex;
  }, [displayCart, displayCart?.items, activeRewardBarSection?.setting]);

  useEffect(() => {
    if (!showVariantSelector || !open) return undefined;

    if (useEditorPreviewData) {
      setVariantOptionsByHandle((prev) => {
        const next = { ...prev };
        let changed = false;
        for (const { item } of displayItems) {
          const handle = getCartLineProductHandle(item);
          const key = item?.key || item?.id;
          const options = EDITOR_PREVIEW_VARIANT_OPTIONS[key];
          if (!handle || !options?.length || next[handle]) continue;
          next[handle] = options;
          changed = true;
        }
        return changed ? next : prev;
      });
      return undefined;
    }

    const gen = ++variantHydrateGenRef.current;
    const handles = [
      ...new Set(
        displayItems
          .map(({ item }) => getCartLineProductHandle(item))
          .filter(Boolean)
      ),
    ].filter((handle) => !variantOptionsByHandle[handle]);

    if (!handles.length) return undefined;

    let cancelled = false;
    (async () => {
      const fetched = {};
      await Promise.all(
        handles.map(async (handle) => {
          try {
            const product = await cartService.fetchProductByHandle(handle);
            const options = buildVariantSelectOptionsFromProduct(product);
            if (options.length) fetched[handle] = options;
          } catch (err) {
            console.warn(
              "[CustomCartDrawer] Variant options hydrate failed:",
              handle,
              err
            );
          }
        })
      );
      if (cancelled || gen !== variantHydrateGenRef.current) return;
      if (!Object.keys(fetched).length) return;
      setVariantOptionsByHandle((prev) => ({ ...prev, ...fetched }));
    })();

    return () => {
      cancelled = true;
    };
    // variantOptionsByHandle read intentionally — filter skips cached handles
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showVariantSelector, open, useEditorPreviewData, displayItems]);

  const seenFreeGiftKeysRef = useRef(new Set());
  useEffect(() => {
    if (!open || embeddedInEditor || useEditorPreviewData) return;
    const rewardSetting = activeRewardBarSection?.setting;
    if (rewardSetting?.fireConfettiOnFreeGiftUnlock === false) return;

    const currentKeys = new Set();
    let shouldFire = false;

    for (const { item } of displayItems) {
      if (!isFreeGiftRewardLine(item, rewardSetting, displayCart)) continue;
      const key = String(item?.key || `${item?.variant_id || ""}:${item?.id || ""}`);
      if (!key) continue;
      currentKeys.add(key);
      if (!seenFreeGiftKeysRef.current.has(key)) {
        seenFreeGiftKeysRef.current.add(key);
        shouldFire = true;
      }
    }

    for (const key of [...seenFreeGiftKeysRef.current]) {
      if (!currentKeys.has(key)) seenFreeGiftKeysRef.current.delete(key);
    }

    if (shouldFire) {
      requestAnimationFrame(() => {
        fireFreeGiftConfetti(confettiCanvasRef.current);
      });
    }
  }, [
    open,
    embeddedInEditor,
    useEditorPreviewData,
    displayItems,
    displayCart,
    activeRewardBarSection?.setting,
  ]);

  const billSummary = useMemo(
    () =>
      buildCartBillSummary(displayCart, {
        rewardSection: activeRewardBarSection,
        formatMoney: (cents) => formatStorefrontMoneyFromCents(cents, appState),
        discountCodeLabels,
        totalMrpLabel,
        deliveryFeeLabel,
        deliveryFeeText,
        mrpDiscountLabel,
        couponDiscountLabel,
        grandTotalLabel,
        showDiscounts: showDiscount,
        discountLabel: discountLabelText,
      }),
    [
      displayCart,
      activeRewardBarSection,
      appState,
      discountCodeLabels,
      totalMrpLabel,
      deliveryFeeLabel,
      deliveryFeeText,
      mrpDiscountLabel,
      couponDiscountLabel,
      grandTotalLabel,
      showDiscount,
      discountLabelText,
      billSummaryOpen,
    ]
  );

  const displaySubtotalCents = useMemo(
    () =>
      Number(
        billSummary?.payableTotalCents ?? displayCart?.total_price
      ) || 0,
    [billSummary?.payableTotalCents, displayCart?.total_price]
  );

  const upsellConditionsMet = useMemo(() => {
    if (isAdminPreview) {
      return true;
    }
    return shouldShowCartDrawerUpsellBlock(popupData, {
      cart: displayCart,
    });
  }, [
    isAdminPreview,
    popupData,
    popupData?.generalSettings?.condition?.ecommerce,
    displayCart,
    displayCart?.items,
    displayCart?.total_price,
    displayCart?.item_count,
  ]);

  const { isSyncing: isRewardGiftSyncing } = useRewardBarRuntime({
    rewardSection: activeRewardBarSection,
    cart: displayCart,
    trackingContext: rewardTrackingContext,
    enabled: showRewardBar && open && !embeddedInEditor && !useEditorPreviewData,
    onCartUpdated: applyCartSnapshot,
  });

  const freeGiftPicker = useFreeGiftPickerController({
    cart: displayCart,
    rewardSetting: activeRewardBarSection?.setting,
    enabled: showRewardBar && open && !embeddedInEditor && !useEditorPreviewData,
  });

  const rewardSetting = activeRewardBarSection?.setting || {};

  // Celebrate when the free-gift picker sheet opens (respects merchant toggle).
  useEffect(() => {
    if (!freeGiftPicker.open || embeddedInEditor || useEditorPreviewData) return;
    if (rewardSetting.fireConfettiOnFreeGiftUnlock === false) return;
    requestAnimationFrame(() => {
      fireFreeGiftConfetti(confettiCanvasRef.current);
    });
  }, [
    freeGiftPicker.open,
    embeddedInEditor,
    useEditorPreviewData,
    rewardSetting.fireConfettiOnFreeGiftUnlock,
  ]);

  const showFreeGiftReopenBanner =
    isSpecificProductsRewardMode(rewardSetting) &&
    rewardSetting.showFreeGiftReopenBanner !== false &&
    freeGiftPicker.canReopen;
  const freeGiftChangeOnly =
    freeGiftPicker.canChangeGift && !freeGiftPicker.needingTierCount;
  const freeGiftReopenBannerText =
    String(rewardSetting.freeGiftReopenBannerText || "").trim() ||
    (freeGiftChangeOnly
      ? "Want a different free gift? Tap to change."
      : DEFAULT_FREE_GIFT_REOPEN_BANNER_TEXT);
  const freeGiftReopenBannerButtonText = String(
    rewardSetting.freeGiftReopenBannerButtonText ??
      (freeGiftChangeOnly
        ? "Change gift"
        : DEFAULT_FREE_GIFT_REOPEN_BANNER_BUTTON)
  ).trim();
  const freeGiftReopenBannerBackground =
    rewardSetting.freeGiftReopenBannerBackground || "#FEF3C7";
  const freeGiftReopenBannerColor =
    rewardSetting.freeGiftReopenBannerColor || "#92400E";

  useCartDrawerUpsellDiscountSync({
    popup: popupData,
    cart: displayCart,
    enabled: open && !embeddedInEditor && !useEditorPreviewData,
    onCartUpdated: applyCartSnapshot,
  });

  const lineItemControlsEnabled = !isAdminPreview;
  const lineItemProductLinkEnabled =
    isLinkToProductEnabled(popupData) && !isAdminPreview;
  const lineItemProductLinkOpenInNewTab = useMemo(
    () => isLinkToProductOpenInNewTab(popupData),
    [popupData]
  );

  const openLineItemProductPage = useCallback((item, event) => {
    event?.preventDefault?.();
    event?.stopPropagation?.();
    const url = resolveCartLineProductUrl(item);
    if (url) {
      openProductPage(url, { newTab: lineItemProductLinkOpenInNewTab });
    }
  }, [lineItemProductLinkOpenInNewTab]);

  const itemCount = displayItems.reduce(
    (sum, entry) => sum + (Number(entry?.item?.quantity) || 0),
    0
  );
  const currency = resolveCartCurrencyCode(displayCart || cartData, "USD");
  const showItemsSubtitle = layout.header?.showItemsSubtitle === true;
  const itemsSubtitle = showItemsSubtitle
    ? (layout.header?.itemsSubtitleTemplate || "{count} items in your cart").replace(
        /\{count\}/g,
        String(itemCount)
      )
    : null;
  const rawCartTitle =
    titleSection && Object.prototype.hasOwnProperty.call(titleSection, "text")
      ? titleSection.text
      : layout.header?.cartTitle;
  const cartTitleText = String(rawCartTitle ?? "").trim();
  // Default ON so "Your Cart" shows; only hide when merchant explicitly turns it off.
  const showCartTitleText =
    layout.header?.showCartTitle !== false && Boolean(cartTitleText);
  const showHeaderTitleChrome =
    showHeaderTitle &&
    (showCartTitleText ||
      (layout.header?.showItemCount !== false && itemCount > 0) ||
      Boolean(itemsSubtitle));
  const headerTitleRowClassName = showItemsSubtitle
    ? "weu-custom-cart-drawer__header-title-row"
    : "weu-custom-cart-drawer__header-title-row weu-custom-cart-drawer__header-title-row--single-line";
  const headerCopyClassName = showItemsSubtitle
    ? "weu-custom-cart-drawer__header-copy"
    : "weu-custom-cart-drawer__header-copy weu-custom-cart-drawer__header-copy--single-line";
  const showFooter =
    displayItems.length > 0 &&
    (showDiscountCodeInput ||
      showOrderNote ||
      showDiscount ||
      showSubtotalLabel ||
      showSubtotalPrice ||
      showTaxNote ||
      showCheckout);
  const showUpsellStrip =
    showUpsellProduct &&
    upsellConditionsMet &&
    displayItems.length > 0 &&
    (isAdminPreview || (cartData?.items?.length ?? 0) > 0);

  useEffect(() => {
    if (!open || isAdminPreview || embeddedInEditor) return;
    const sectionProducts = Array.isArray(upsellProductSection?.products)
      ? upsellProductSection.products
      : [];
    console.log("[WEU CartUpsell] drawer gate", {
      popupId: popupData?._id,
      showUpsellStrip,
      showUpsellProduct,
      upsellConditionsMet,
      displayItems: displayItems.length,
      cartItems: cartData?.items?.length ?? 0,
      sectionType: upsellProductSection?.type,
      sectionPart: upsellProductSection?.setting?.cartDrawerPart,
      sectionProductCount: sectionProducts.length,
      sectionCatalogReady: sectionProducts.map((p) => ({
        id: p?.id,
        title: p?.title,
        variants: Array.isArray(p?.variants) ? p.variants.length : 0,
      })),
      runtimeResolved: upsellProductSection?.runtimeResolved === true,
      runtimeLoading: popupData?.__runtimeContentLoading === true,
      offerType:
        popupData?.generalSettings?.condition?.product?.products_to_offer?.type,
      offerValueCount: Array.isArray(
        popupData?.generalSettings?.condition?.product?.products_to_offer?.value
      )
        ? popupData.generalSettings.condition.product.products_to_offer.value
            .length
        : 0,
      sectionCount: sections.length,
      upsellProductIndex: STATIC_CART_SECTION_INDEX.UPSELL_PRODUCT,
    });
  }, [
    open,
    isAdminPreview,
    embeddedInEditor,
    showUpsellStrip,
    showUpsellProduct,
    upsellConditionsMet,
    displayItems.length,
    cartData?.items?.length,
    upsellProductSection,
    popupData?._id,
    popupData?.__runtimeContentLoading,
    popupData?.generalSettings?.condition?.product?.products_to_offer,
    sections.length,
  ]);

  const isCartAddPending =
    pendingCartAdd ||
    (typeof window !== "undefined" && window.__WEUPSELL_CART_ADD_PENDING__);
  const cartHasItems = (cartData?.items?.length ?? 0) > 0;
  // Empty cart + add in progress → body spinner (not "Your cart is empty").
  // Already has items → keep showing them; refresh updates after add.js.
  const showFullBodyLoading =
    !useEditorPreviewData &&
    ((isCartAddPending && !cartHasItems) || (loading && cartData == null));
  const closeButtonStyle = {
    ...closeCss,
    backgroundColor: layout.header?.closeButtonBackground || "#f3f4f6",
  };
  const lineBorderColor = lineItemTypography.lineDividerColor;
  const bodyListStyle = {
    "--weu-cart-line-divider": lineBorderColor,
    "--weu-savings-bg": savingsBadgeBackground,
    "--weu-savings-color": savingsColor,
    ...(useLineItemCards
      ? {
          "--weu-line-card-border": lineBorderColor,
          "--weu-line-card-radius": layoutBody.lineItemBorderRadius || "8px",
          "--weu-line-gap": layoutBody.lineItemGap || "8px",
        }
      : {}),
  };

  const shellStyles = useMemo(
    () => getStaticCartDrawerShellStyles(popupData, isMobile),
    [
      popupData,
      isMobile,
      drawerStepIndex,
      drawerStep?.popupSettings,
      drawerStep?.popupSettings?.customCSS,
      drawerStep?.popupSettings?.dynamicalOptions,
      drawerStep?.popupSettings?.zIndex,
      drawerStep?.cartDrawerLayout?.design,
    ]
  );

  useEffect(() => {
    if (!open) setReleaseHostLayer(false);
  }, [open]);

  useEffect(() => {
    if (embeddedInEditor || isAdminPreview || !open) return undefined;
    const host = document.getElementById("popup-preview-shadow-root");
    if (!host) return undefined;

    const previous = host.style.getPropertyValue("z-index");
    const previousPriority = host.style.getPropertyPriority("z-index");
    const merchantZ = Number(shellStyles.rootStyle?.zIndex);
    const nextZ =
      releaseHostLayer
        ? 1
        : Number.isFinite(merchantZ) && merchantZ > 0
          ? merchantZ
          : 2147483647;
    // Never reparent #popup-preview-shadow-root — appendChild reloads shadow
    // CSS for a few frames and flashes unstyled coupon / footer chrome.
    host.style.setProperty("z-index", String(nextZ), "important");

    return () => {
      if (previous) {
        host.style.setProperty("z-index", previous, previousPriority);
      } else {
        host.style.removeProperty("z-index");
      }
    };
  }, [
    embeddedInEditor,
    isAdminPreview,
    open,
    releaseHostLayer,
    shellStyles.rootStyle?.zIndex,
  ]);

  const panelInlineStyle = useMemo(() => {
    const style = { ...shellStyles.panelStyle };
    if (shellStyles.panelBackgroundColor) {
      style["--weu-cart-drawer-panel-bg"] = shellStyles.panelBackgroundColor;
    }
    return style;
  }, [shellStyles]);

  const footerActions = useCartDrawerFooterActions({
    discountSection: discountCodeSection,
    orderNoteSection,
    showDiscountTrigger: showDiscountCodeInput,
    showOrderNoteTrigger: showOrderNote,
    cart: displayCart,
    isMobile,
    embeddedInEditor,
    getEditorPartProps,
    onCartUpdated: applyCartSnapshot,
    onDiscountApplied: handleDiscountApplied,
    formatMoney: (cents) => formatStorefrontMoneyFromCents(cents, appState),
    discountCodeLabels,
    onNoteChange: (value) => {
      pendingOrderNoteRef.current = value;
    },
  });

  const drawerPanel = (
    <FormProvider {...upsellFormMethods}>
    <aside
      className={`weu-custom-cart-drawer__panel${embeddedInEditor ? " weu-custom-cart-drawer__panel--editor" : ""}`}
      style={panelInlineStyle}
      onClick={(e) => e.stopPropagation()}
      role="dialog"
      aria-modal={!embeddedInEditor}
      aria-label={cartTitleText || layout.header?.cartTitle || "Cart"}
    >
      <canvas
        ref={confettiCanvasRef}
        className="weu-custom-cart-drawer__confetti"
        aria-hidden="true"
      />
      {(showHeaderTitleChrome || showHeaderClose) && (
      <header className="weu-custom-cart-drawer__header">
        {showHeaderTitleChrome ? (
        <div
          className="weu-custom-cart-drawer__header-title weu-custom-cart-drawer__part"
          {...getEditorPartProps(STATIC_CART_SECTION_INDEX.HEADER_TITLE)}
        >
          <div className={headerTitleRowClassName}>
            {(showCartTitleText ||
              (layout.header?.showItemCount !== false && itemCount > 0) ||
              itemsSubtitle) && (
            <div className={headerCopyClassName}>
              {(showCartTitleText ||
                (layout.header?.showItemCount !== false && itemCount > 0)) && (
              <div className="weu-custom-cart-drawer__header-title-line">
                {showCartTitleText ? (
                  <h2 className="weu-custom-cart-drawer__title">
                    <span
                      className="weu-custom-cart-drawer__title-text"
                      style={titleCss}
                    >
                      {cartTitleText}
                    </span>
                  </h2>
                ) : null}
                {layout.header?.showItemCount !== false && itemCount > 0 && (
                  <span className="weu-custom-cart-drawer__count-badge">
                    {itemCount}
                  </span>
                )}
              </div>
              )}
              {itemsSubtitle ? (
                <p
                  className="weu-custom-cart-drawer__header-subtitle"
                  style={headerSubtitleStyle}
                >
                  {itemsSubtitle}
                </p>
              ) : null}
            </div>
            )}
          </div>
        </div>
        ) : null}
        {showHeaderClose ? (
        <button
          type="button"
          className="weu-custom-cart-drawer__close weu-custom-cart-drawer__part"
          aria-label={layout.header?.closeAriaLabel || "Close cart"}
          {...(embeddedInEditor
            ? getEditorPartProps(STATIC_CART_SECTION_INDEX.HEADER_CLOSE, closeButtonStyle)
            : { style: closeButtonStyle, onClick: () => onClose?.() })}
        >
          <CloseIcon />
        </button>
        ) : null}
      </header>
      )}

      {showAnnouncementBar && announcementText ? (
        <div
          className="weu-custom-cart-drawer__announcement weu-custom-cart-drawer__part"
          {...getEditorPartProps(
            STATIC_CART_SECTION_INDEX.ANNOUNCEMENT_BAR,
            announcementStyle
          )}
        >
          <span
            key={`${announcementIndex}-${announcementText}`}
            className={`weu-custom-cart-drawer__announcement-text${
              announcementFading
                ? " weu-custom-cart-drawer__announcement-text--fade-out"
                : " weu-custom-cart-drawer__announcement-text--fade-in"
            }`}
          >
            {announcementText}
          </span>
        </div>
      ) : null}

      {showHeaderDivider && displayItems.length > 0 ? (
      <div
        className="weu-custom-cart-drawer__divider-wrap weu-custom-cart-drawer__part"
        {...getEditorPartProps(STATIC_CART_SECTION_INDEX.HEADER_DIVIDER)}
      >
        <DividerBlock section={dividerSection} fullWidth />
      </div>
      ) : null}

      {showRewardBar ? (
        <div className="weu-custom-cart-drawer__reward-slot">
          <RewardBar
            section={rewardBarSection}
            cart={displayCart}
            formatMoney={(cents) => formatStorefrontMoneyFromCents(cents, appState)}
            embeddedInEditor={embeddedInEditor}
            isMobile={isMobile}
            editorPartProps={getEditorPartProps(STATIC_CART_SECTION_INDEX.REWARD_BAR)}
          />
        </div>
      ) : null}

      {showRewardBar && showBody ? (
        <div
          className="weu-custom-cart-drawer__section-divider"
          style={{ "--weu-section-divider-color": lineBorderColor }}
          role="separator"
          aria-hidden
        />
      ) : null}

      {(showBody || showUpsellStrip) ? (
      <div className="weu-custom-cart-drawer__scroll">
        {showFullBodyLoading ? (
          <div className="weu-custom-cart-drawer__loading">
            <span className="weu-custom-cart-drawer__spinner" />
          </div>
        ) : (
          <div
            className={`weu-custom-cart-drawer__scroll-inner${!displayItems.length ? " weu-custom-cart-drawer__scroll-inner--empty" : ""}`}
          >
            {showBody ? (
            <div
              className={`weu-custom-cart-drawer__body weu-custom-cart-drawer__part${!displayItems.length ? " weu-custom-cart-drawer__body--empty" : ""}`}
              {...getEditorPartProps(STATIC_CART_SECTION_INDEX.BODY, bodyCss)}
            >
              {!displayItems.length ? (
                <div className="weu-custom-cart-drawer__empty">
                  {showEmptyCartIcon ? <EmptyCartIcon /> : null}
                  <p
                    className="weu-custom-cart-drawer__empty-message"
                    style={lineItemTypography.empty}
                  >
                    {emptyMessage}
                  </p>
                  {showContinueShopping ? (
                    <button
                      type="button"
                      className="weu-custom-cart-drawer__continue-shopping"
                      style={continueShoppingButtonStyle}
                      onClick={handleContinueShopping}
                    >
                      {continueShoppingLabel}
                    </button>
                  ) : null}
                </div>
              ) : (
                <ul
                  className={`weu-custom-cart-drawer__lines${
                    useLineItemCards ? " weu-custom-cart-drawer__lines--cards" : ""
                  }`}
                  style={bodyListStyle}
                >
                  {displayItems.map(({ item, cartLineIndex }) => {
              const lineKey = item?.key || item?.id || cartLineIndex;
              const variantLabel = getVariantLabel(item, variantSeparator);
              const sellingPlanLabel = getSellingPlanLabel(item);
              const lineBusy = loadingLines.includes(lineKey);
              // Pass cart so order/cart-level % stays off the row price
              // (Bill Summary owns those) — avoids ₹540 when bill also shows 10% OFF.
              const pricing = getCartLineItemPricing(item, displayCart);
              const lineDiscountLabels = showLineDiscountChip
                ? getCartLineDiscountLabels(item, displayCart)
                : [];
              const rewardMatch = findRewardProductForLine(
                item,
                activeRewardBarSection?.setting
              );
              const isTaggedFreeGift = isFreeGiftRewardLine(
                item,
                activeRewardBarSection?.setting,
                displayCart
              );
              const isPendingFreeGift =
                Boolean(isRewardGiftSyncing) &&
                isPendingAutoClaimGiftLine(
                  item,
                  displayCart,
                  activeRewardBarSection?.setting
                );
              const isFreeGift = isTaggedFreeGift || isPendingFreeGift;
              // Tag/collection/vendor/title pools often lack discount_value on
              // resolved stubs; always force FREE chrome for free-gift lines.
              const giftPricing = (() => {
                if (rewardMatch?.product) {
                  return resolveRewardGiftLineDisplayPricing(
                    item,
                    rewardMatch.product,
                    { forceFreeGift: isFreeGift }
                  );
                }
                if (isFreeGift) {
                  return {
                    isFree: true,
                    showDiscount: true,
                    finalLine: 0,
                    originalLine: Number(
                      item?.original_line_price ??
                        item?.line_price ??
                        item?.final_line_price ??
                        0
                    ),
                  };
                }
                return null;
              })();
              // getCartLineItemPricing returns finalLine / originalLine (not display*).
              const displayFinalLine = giftPricing
                ? giftPricing.finalLine
                : pricing.finalLine;
              const displayOriginalLine = giftPricing
                ? Math.max(
                    giftPricing.originalLine,
                    pricing.originalLine,
                    pricing.compareAtLine
                  )
                : pricing.originalLine;
              const showGiftDiscount =
                Boolean(giftPricing?.showDiscount) ||
                (showLineComparePrice &&
                  displayOriginalLine > displayFinalLine);
              // Prefer real product image; if none, use free-gift fallback (avoid blank thumb)
              const freeGiftFallback = isFreeGift
                ? getRewardProductImageUrl(rewardMatch?.product) ||
                  FREE_GIFT_FALLBACK_IMAGE
                : "";
              const productTitle =
                item.product_title ||
                item.title ||
                rewardMatch?.product?.title ||
                "";

              const priceBlock = (
                <div className="weu-custom-cart-drawer__line-prices">
                  {isPendingFreeGift ? (
                    <span
                      className="weu-custom-cart-drawer__gift-applying"
                      aria-live="polite"
                      aria-busy="true"
                    >
                      <span
                        className="weu-custom-cart-drawer__gift-applying-spinner"
                        aria-hidden="true"
                      />
                      Applying gift…
                    </span>
                  ) : (
                    <>
                      <span
                        className={`weu-custom-cart-drawer__line-price-final${
                          giftPricing?.isFree
                            ? " weu-custom-cart-drawer__line-price-final--free"
                            : ""
                        }`}
                        style={lineItemTypography.price}
                      >
                        {giftPricing?.isFree
                          ? "FREE"
                          : formatStorefrontMoneyFromCents(
                              displayFinalLine,
                              appState
                            )}
                      </span>
                      {showGiftDiscount &&
                      displayOriginalLine > displayFinalLine ? (
                        <span
                          className="weu-custom-cart-drawer__line-price-compare"
                          style={lineItemTypography.comparePrice}
                        >
                          {formatStorefrontMoneyFromCents(
                            displayOriginalLine,
                            appState
                          )}
                        </span>
                      ) : null}
                    </>
                  )}
                </div>
              );

              const qtyBlock =
                !isFreeGift && showQuantityControls ? (
                  <div
                    className={`weu-custom-cart-drawer__qty${
                      lineBusy ? " weu-custom-cart-drawer__qty--busy" : ""
                    }${!lineItemControlsEnabled ? " weu-custom-cart-drawer__qty--readonly" : ""}`}
                    style={{
                      ["--weu-qty-color"]: lineItemTypography.quantity?.color,
                      ["--weu-qty-bg"]: lineItemTypography.quantity?.background,
                      fontSize: lineItemTypography.quantity?.fontSize,
                      color: lineItemTypography.quantity?.color,
                      background: lineItemTypography.quantity?.background,
                      borderWidth: lineItemTypography.quantity?.borderWidth
                        ? `${lineItemTypography.quantity.borderWidth}px`
                        : undefined,
                      borderStyle: lineItemTypography.quantity?.borderWidth
                        ? "solid"
                        : undefined,
                      borderColor: lineItemTypography.quantity?.borderColor,
                      borderRadius: lineItemTypography.quantity?.borderRadius,
                    }}
                  >
                    {lineItemControlsEnabled && lineBusy ? (
                      <span
                        className="weu-custom-cart-drawer__qty-loading"
                        aria-hidden="true"
                      >
                        <span className="weu-custom-cart-drawer__spinner weu-custom-cart-drawer__spinner--xs" />
                      </span>
                    ) : (
                      <>
                        <button
                          type="button"
                          className="weu-custom-cart-drawer__qty-btn"
                          onClick={() =>
                            updateQuantity(item, Math.max(0, item.quantity - 1))
                          }
                          aria-label="Decrease quantity"
                          disabled={!lineItemControlsEnabled || lineBusy}
                        >
                          <MinusIcon />
                        </button>
                        <span className="weu-custom-cart-drawer__qty-value">
                          {item.quantity}
                        </span>
                        <button
                          type="button"
                          className="weu-custom-cart-drawer__qty-btn"
                          onClick={() => updateQuantity(item, item.quantity + 1)}
                          aria-label="Increase quantity"
                          disabled={!lineItemControlsEnabled || lineBusy}
                        >
                          <PlusIcon />
                        </button>
                      </>
                    )}
                  </div>
                ) : null;

              const removeButton =
                !isFreeGift && showRemoveButton ? (
                  <button
                    type="button"
                    className={`weu-custom-cart-drawer__remove${
                      lineBusy ? " weu-custom-cart-drawer__remove--loading" : ""
                    }`}
                    onClick={() => updateQuantity(item, 0)}
                    aria-label="Remove item"
                    disabled={!lineItemControlsEnabled || lineBusy}
                  >
                    {lineBusy ? (
                      <span
                        className="weu-custom-cart-drawer__spinner weu-custom-cart-drawer__spinner--xs"
                        aria-hidden="true"
                      />
                    ) : (
                      <DeleteIcon />
                    )}
                  </button>
                ) : null;

              return (
                <li
                  key={lineKey}
                  className={`weu-custom-cart-drawer__line${
                    useLineItemCards ? " weu-custom-cart-drawer__line--card" : ""
                  }${isFreeGift ? " weu-custom-cart-drawer__line--free-gift" : ""}`}
                >
                  <div
                    className={`weu-custom-cart-drawer__thumb-wrap${
                      lineItemProductLinkEnabled && !isFreeGift
                        ? " weu-custom-cart-drawer__line-link"
                        : ""
                    }`}
                    {...(lineItemProductLinkEnabled && !isFreeGift
                      ? {
                          role: "link",
                          tabIndex: 0,
                          onClick: (event) => openLineItemProductPage(item, event),
                          onKeyDown: (event) => {
                            if (event.key === "Enter" || event.key === " ") {
                              openLineItemProductPage(item, event);
                            }
                          },
                        }
                      : {})}
                  >
                    <CartLineThumb
                      item={item}
                      fallbackSrc={freeGiftFallback}
                      showGiftPlaceholder={isFreeGift}
                    />
                    {!isFreeGift &&
                      showLineComparePrice &&
                      pricing.showDiscount &&
                      pricing.percentOff > 0 && (
                        <div
                          className="weu-custom-cart-drawer__badge-container"
                          aria-label={`${pricing.percentOff}% off`}
                        >
                          <img
                            className="weu-custom-cart-drawer__discount-badge"
                            src={DISCOUNT_BADGE_SRC}
                            alt=""
                            aria-hidden="true"
                            draggable={false}
                          />
                          <div className="weu-custom-cart-drawer__badge-text">
                            <span className="weu-custom-cart-drawer__badge-percentage">
                              {pricing.percentOff}%
                            </span>
                            <span className="weu-custom-cart-drawer__badge-off">
                              OFF
                            </span>
                          </div>
                        </div>
                      )}
                  </div>

                  {/* Title / variant / selling plan (beside thumb) */}
                  <div className="weu-custom-cart-drawer__line-main">
                    <div className="weu-custom-cart-drawer__line-details">
                      <p
                        className="weu-custom-cart-drawer__product-title"
                        style={lineItemTypography.title}
                      >
                        {lineItemProductLinkEnabled && !isFreeGift ? (
                          <span
                            className="weu-custom-cart-drawer__line-link weupsell-product-title-link"
                            role="link"
                            tabIndex={0}
                            onClick={(event) => openLineItemProductPage(item, event)}
                            onKeyDown={(event) => {
                              if (event.key === "Enter" || event.key === " ") {
                                openLineItemProductPage(item, event);
                              }
                            }}
                          >
                            {productTitle}
                          </span>
                        ) : (
                          productTitle
                        )}
                      </p>
                      {isFreeGift ? (
                        <span className="weu-custom-cart-drawer__free-gift-badge-row">
                          <span className="weu-custom-cart-drawer__free-gift-badge">
                            <GiftBadgeIcon />
                            {isPendingFreeGift ? "Applying gift…" : "Free Gift"}
                          </span>
                          {!isPendingFreeGift &&
                          freeGiftPicker.canChangeGift &&
                          !embeddedInEditor ? (
                            <button
                              type="button"
                              className="weu-custom-cart-drawer__free-gift-change"
                              onClick={() => freeGiftPicker.reopen()}
                            >
                              Change
                            </button>
                          ) : null}
                        </span>
                      ) : null}
                      {lineDiscountLabels.length > 0 ? (
                        <span className="weu-custom-cart-drawer__line-discount-row">
                          {lineDiscountLabels.map((label) => (
                            <span
                              key={label}
                              className="weu-custom-cart-drawer__line-discount-chip"
                            >
                              {label}
                            </span>
                          ))}
                        </span>
                      ) : null}
                      {!isFreeGift ? (
                        <CartLineVariantRow
                          item={item}
                          lineKey={lineKey}
                          variantLabel={variantLabel}
                          variantStyle={lineItemTypography.variant}
                          showVariantSelector={showVariantSelector}
                          variantOptionsByHandle={variantOptionsByHandle}
                          lineBusy={lineBusy}
                          controlsEnabled={
                            lineItemControlsEnabled || isAdminPreview
                          }
                          onVariantChange={changeLineVariant}
                          showVariantOutOfStockBadge={
                            showVariantOutOfStockBadge
                          }
                          variantOutOfStockBadgeText={
                            variantOutOfStockBadgeText
                          }
                          variantOutOfStockBadgeStyle={
                            variantOutOfStockBadgeStyle
                          }
                        />
                      ) : null}
                      {!isFreeGift && sellingPlanLabel ? (
                        <p
                          className="weu-custom-cart-drawer__selling-plan"
                          style={lineItemTypography.variant}
                        >
                          {sellingPlanLabel}
                        </p>
                      ) : null}
                    </div>
                  </div>

                  {/* Price / compare / qty / delete (right) */}
                  <div className="weu-custom-cart-drawer__line-controls">
                    {priceBlock}
                    {!isFreeGift ? (
                      <div className="weu-custom-cart-drawer__line-actions">
                        {removeButton}
                        {qtyBlock}
                      </div>
                    ) : null}
                  </div>
                </li>
              );
            })}
                </ul>
              )}
            </div>
            ) : null}

            {showUpsellStrip ? (
              <div className="weu-custom-cart-drawer__upsell-anchor">
                <CartDrawerUpsellStrip
                  popupData={popupData}
                  headingSection={upsellHeadingSection}
                  productSection={upsellProductSection}
                  activeStep={drawerStepIndex}
                  isPreviewMode={isAdminPreview}
                  isMobile={isMobile}
                  embeddedInEditor={embeddedInEditor}
                  getEditorPartProps={getEditorPartProps}
                  headingPartIndex={STATIC_CART_SECTION_INDEX.UPSELL_HEADING}
                  productPartIndex={STATIC_CART_SECTION_INDEX.UPSELL_PRODUCT}
                />
              </div>
            ) : null}
          </div>
        )}
      </div>
      ) : null}

      {showFooter && (
        <footer
          className="weu-custom-cart-drawer__footer"
          style={
            shellStyles.panelBackgroundColor
              ? { backgroundColor: shellStyles.panelBackgroundColor }
              : undefined
          }
        >
          <div
            className="weu-custom-cart-drawer__footer-inner"
            style={{
              "--weu-discount-amount-color": discountAmountColor,
              "--weu-savings-banner-bg": savingsBannerBackground,
              "--weu-savings-banner-color": savingsBannerColor,
            }}
          >
            {footerActions.triggers}

            {showFreeGiftReopenBanner ? (
              <button
                type="button"
                className="weu-custom-cart-drawer__free-gift-reopen"
                style={{
                  backgroundColor: freeGiftReopenBannerBackground,
                  color: freeGiftReopenBannerColor,
                }}
                onClick={() => freeGiftPicker.reopen()}
              >
                <span className="weu-custom-cart-drawer__free-gift-reopen-text">
                  {freeGiftReopenBannerText}
                </span>
                {freeGiftReopenBannerButtonText ? (
                  <span className="weu-custom-cart-drawer__free-gift-reopen-cta">
                    {freeGiftReopenBannerButtonText}
                  </span>
                ) : null}
              </button>
            ) : null}

            {(showSubtotalLabel || showSubtotalPrice || showCheckout) && (
              <div className="weu-custom-cart-drawer__checkout-bar weu-custom-cart-drawer__checkout-bar--stacked">
                {(showSubtotalLabel || showSubtotalPrice) &&
                layout.footer?.showSubtotal !== false ? (
                  <button
                    type="button"
                    className="weu-custom-cart-drawer__estimated-total weu-custom-cart-drawer__part"
                    disabled={embeddedInEditor ? false : !displayItems.length}
                    aria-expanded={billSummaryOpen}
                    aria-label={`${subtotalLabelText}, open bill summary`}
                    style={{
                      "--weu-savings-bg": savingsBadgeBackground,
                      "--weu-savings-color": savingsColor,
                    }}
                    {...getEditorPartProps(
                      STATIC_CART_SECTION_INDEX.FOOTER_SUBTOTAL_LABEL,
                      subtotalLabelCss
                    )}
                    onClick={(event) => {
                      if (embeddedInEditor) {
                        event.stopPropagation();
                        selectEditorPart(
                          STATIC_CART_SECTION_INDEX.FOOTER_SUBTOTAL_LABEL
                        );
                      }
                      setBillSummaryOpen((open) => !open);
                    }}
                  >
                    <span className="weu-custom-cart-drawer__estimated-total-row">
                      {showSubtotalLabel ? (
                        <span className="weu-custom-cart-drawer__estimated-total-label">
                          <svg
                            className="weu-custom-cart-drawer__estimated-total-icon"
                            viewBox="0 0 24 24"
                            fill="currentColor"
                            aria-hidden
                          >
                            <path d="M14 2H6c-1.1 0-2 .9-2 2v16c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V8l-6-6zm2 16H8v-2h8v2zm0-4H8v-2h8v2zm-3-5V3.5L18.5 9H13z" />
                          </svg>
                          <span
                            style={
                              embeddedInEditor ? subtotalLabelCss : undefined
                            }
                          >
                            {subtotalLabelText}
                          </span>
                        </span>
                      ) : null}
                      {showSubtotalPrice ? (
                        <span
                          className="weu-custom-cart-drawer__estimated-total-prices weu-custom-cart-drawer__part"
                          {...getEditorPartProps(
                            STATIC_CART_SECTION_INDEX.FOOTER_SUBTOTAL_PRICE,
                            subtotalPriceCss
                          )}
                        >
                          {billSummary.showCompareAtTotal ? (
                            <span className="weu-custom-cart-drawer__estimated-total-compare">
                              {formatStorefrontMoneyFromCents(
                                billSummary.compareAtTotalCents,
                                appState
                              )}
                            </span>
                          ) : null}
                          <span
                            className="weu-custom-cart-drawer__estimated-total-value"
                            style={subtotalPriceCss}
                          >
                            {formatStorefrontMoneyFromCents(
                              displaySubtotalCents,
                              appState
                            )}
                          </span>
                          <svg
                            className={`weu-custom-cart-drawer__estimated-total-chevron${
                              billSummaryOpen
                                ? " weu-custom-cart-drawer__estimated-total-chevron--open"
                                : ""
                            }`}
                            viewBox="0 0 24 24"
                            fill="currentColor"
                            aria-hidden
                          >
                            <path d="M7.41 8.59 12 13.17l4.59-4.58L18 10l-6 6-6-6z" />
                          </svg>
                        </span>
                      ) : null}
                    </span>
                    {billSummary.totalSavingsCents > 0 ? (
                      <span className="weu-custom-cart-drawer__estimated-total-saved">
                        {formatSavingsBannerText(
                          estimatedTotalSavedText,
                          formatStorefrontMoneyFromCents(
                            billSummary.totalSavingsCents,
                            appState
                          )
                        )}
                      </span>
                    ) : null}
                  </button>
                ) : null}

                {showTerms ? (
                  <div className="weu-custom-cart-drawer__terms">
                    <label className="weu-custom-cart-drawer__terms-label">
                      <input
                        type="checkbox"
                        className="weu-custom-cart-drawer__terms-input"
                        checked={termsAccepted}
                        onChange={(event) => {
                          setTermsAccepted(event.target.checked);
                        }}
                      />
                      <span>
                        {termsAgreement.prefix ? (
                          <span>{termsAgreement.prefix} </span>
                        ) : null}
                        <TermsAgreementLink
                          label={termsAgreement.termsLabel}
                          href={termsAgreement.termsUrl}
                          embeddedInEditor={embeddedInEditor}
                        />
                        {termsAgreement.privacyLabel ? (
                          <>
                            <span> {termsAgreement.joiner} </span>
                            <TermsAgreementLink
                              label={termsAgreement.privacyLabel}
                              href={termsAgreement.privacyUrl}
                              embeddedInEditor={embeddedInEditor}
                            />
                          </>
                        ) : null}
                      </span>
                    </label>
                    {!embeddedInEditor && !termsAccepted ? (
                      <p className="weu-custom-cart-drawer__terms-error" role="alert">
                        Please agree before checkout.
                      </p>
                    ) : null}
                  </div>
                ) : null}

                {showCheckout ? (
                  <button
                    type="button"
                    className={`weu-custom-cart-drawer__checkout weu-custom-cart-drawer__part${
                      checkoutLoading
                        ? " weu-custom-cart-drawer__checkout--loading"
                        : ""
                    }`}
                    disabled={
                      !embeddedInEditor &&
                      (!displayItems.length ||
                        checkoutLoading ||
                        (showTerms && !termsAccepted))
                    }
                    aria-busy={checkoutLoading}
                    {...(embeddedInEditor
                      ? getEditorPartProps(
                          STATIC_CART_SECTION_INDEX.FOOTER_CHECKOUT,
                          checkoutButtonStyle
                        )
                      : {
                          style: checkoutButtonStyle,
                          onClick: handleCheckout,
                        })}
                  >
                    {checkoutLoading ? (
                      <>
                        <span
                          className="weu-custom-cart-drawer__spinner weu-custom-cart-drawer__spinner--sm"
                          aria-hidden
                        />
                        <span>Processing…</span>
                      </>
                    ) : (
                      <span className="weu-custom-cart-drawer__checkout-inner">
                        {showCheckoutLock ? (
                          <span className="weu-custom-cart-drawer__checkout-icon">
                            {checkoutLeftIconUrl ? (
                              <img
                                src={checkoutLeftIconUrl}
                                alt=""
                                className="weu-custom-cart-drawer__checkout-icon-img"
                              />
                            ) : (
                              <LockIcon />
                            )}
                          </span>
                        ) : null}
                        <span className="weu-custom-cart-drawer__checkout-label">
                          {checkoutLabelText}
                        </span>
                        {showCheckoutSparkle ? (
                          <span className="weu-custom-cart-drawer__checkout-icon weu-custom-cart-drawer__checkout-icon--sparkle">
                            {checkoutRightIconUrl ? (
                              <img
                                src={checkoutRightIconUrl}
                                alt=""
                                className="weu-custom-cart-drawer__checkout-icon-img"
                              />
                            ) : (
                              <SparkleIcon />
                            )}
                          </span>
                        ) : null}
                      </span>
                    )}
                  </button>
                ) : null}
              </div>
            )}

            <div
              className="weu-custom-cart-drawer__brand-attribution"
              data-weupsell-brand-attribution="cart-drawer"
            >
              <AiBrandAttribution />
            </div>
          </div>
        </footer>
      )}
      {footerActions.sheets}
      <FreeGiftPickerSheet
        open={freeGiftPicker.open}
        onClose={freeGiftPicker.close}
        cart={displayCart}
        rewardSetting={activeRewardBarSection?.setting}
        trackingContext={rewardTrackingContext}
        formatMoney={(cents) =>
          formatStorefrontMoneyFromCents(cents, appState)
        }
        onCartUpdated={applyCartSnapshot}
        embeddedInEditor={embeddedInEditor}
        showVariantOutOfStockBadge={showVariantOutOfStockBadge}
        variantOutOfStockBadgeText={variantOutOfStockBadgeText}
        variantOutOfStockBadgeStyle={variantOutOfStockBadgeStyle}
      />
      <CartBillSummarySheet
        open={billSummaryOpen}
        onClose={() => setBillSummaryOpen(false)}
        cart={displayCart}
        formatMoney={(cents) =>
          formatStorefrontMoneyFromCents(cents, appState)
        }
        rewardSection={activeRewardBarSection}
        discountCodeLabels={discountCodeLabels}
        embeddedInEditor={embeddedInEditor}
        showSavingsBanner={showSavingsBanner}
        savingsBannerBackground={savingsBannerBackground}
        savingsBannerColor={savingsBannerColor}
        savingsBannerText={savingsBannerText}
        billSummaryTitle={billSummaryTitle}
        billSummaryTitleStyle={billSummaryTitleStyle}
        billSummaryLineStyle={billSummaryLineStyle}
        billSummaryMetaLineStyle={billSummaryMetaLineStyle}
        detailLayout={billSummaryDetailLayout}
        sellingSubtotalLabel={sellingSubtotalLabel}
        totalMrpLabel={totalMrpLabel}
        deliveryFeeLabel={deliveryFeeLabel}
        deliveryFeeText={deliveryFeeText}
        mrpDiscountLabel={mrpDiscountLabel}
        couponDiscountLabel={couponDiscountLabel}
        grandTotalLabel={grandTotalLabel}
        showDiscounts={showDiscount}
        discountAmountColor={discountAmountColor}
        removingDiscount={removingDiscount}
        onRemoveDiscount={handleRemoveDiscount}
        taxNoteText={showTaxNote ? taxNoteText : ""}
        taxNoteStyle={taxNoteStyle}
        showCheckout={showCheckout}
        checkoutLabel={checkoutLabelText}
        checkoutButtonStyle={checkoutButtonStyle}
        checkoutLoading={checkoutLoading}
        checkoutDisabled={!displayItems.length}
        showCheckoutLock={showCheckoutLock}
        showCheckoutSparkle={showCheckoutSparkle}
        checkoutLeftIcon={
          checkoutLeftIconUrl ? (
            <img
              src={checkoutLeftIconUrl}
              alt=""
              className="weu-custom-cart-drawer__checkout-icon-img"
            />
          ) : (
            <LockIcon />
          )
        }
        checkoutRightIcon={
          checkoutRightIconUrl ? (
            <img
              src={checkoutRightIconUrl}
              alt=""
              className="weu-custom-cart-drawer__checkout-icon-img"
            />
          ) : (
            <SparkleIcon />
          )
        }
        onCheckout={() => {
          setBillSummaryOpen(false);
          handleCheckout();
        }}
      />
    </aside>
    </FormProvider>
  );

  // Existing carts (no layout.design) keep Inter — do not inherit theme fonts
  // that break reward-bar / chrome layout. Design tab "theme-default" may inherit.
  const designFontCss =
    designTokens.fontFamily && designTokens.fontFamily !== "theme-default"
      ? designTokens.fontFamily
      : designTokens.hasExplicitDesign
        ? "inherit"
        : '"Inter", "Segoe UI", system-ui, -apple-system, sans-serif';

  const renderShell = (modeClass) => (
    <div
      className={`weu-custom-cart-drawer ${modeClass}`}
      style={{
        ...shellStyles.rootStyle,
        "--weu-design-primary": designTokens.primaryText,
        "--weu-design-secondary": designTokens.secondaryText,
        "--weu-design-price": designTokens.priceColor,
        "--weu-design-radius": `${designTokens.cornerRadius}px`,
        "--weu-design-shell-radius": `${designTokens.shellCornerRadius}px`,
        "--weu-design-bg": designTokens.backgroundColor,
        "--weu-design-border": designTokens.borderColor,
        "--weu-design-font": designFontCss,
        fontFamily: designFontCss,
      }}
      role={embeddedInEditor ? undefined : "presentation"}
    >
      {safeMerchantCss ? (
        <style data-weu-cart-merchant-css="1">{safeMerchantCss}</style>
      ) : null}
      {!shellStyles.hideBackdrop && !isAdminPreview && (
        <button
          type="button"
          className="weu-custom-cart-drawer__backdrop"
          style={shellStyles.backdropStyle}
          aria-label="Close cart overlay"
          onClick={onClose}
        />
      )}
      {drawerPanel}
    </div>
  );

  if (embeddedInEditor && !open) {
    return null;
  }

  if (embeddedInEditor) {
    return renderShell("weu-custom-cart-drawer--editor");
  }

  if (!open) return null;

  return renderShell("weu-custom-cart-drawer--live");
};

export default CustomCartDrawer;
